import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Info, MoreVertical, Undo2, UploadCloud } from 'lucide-react';
import { analytics } from '@/services/analytics';
import { registerStepLeaveGuard } from '@/services/stepLeaveGuard';
import { logging } from '@/services/logging';
import { Order, type OrderStep, type SupervisorEvent } from '@/types/supervisor';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  removeOrderItemById,
  selectOrderItems,
  selectSelectedItemIds,
  setItemSelection,
  updateOrderItem,
  persistDirtyOrderItems,
  selectOrderPricingCacheVersion,
} from '@/features/order/orderSlice';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { deleteOrderItem } from '@/services/orderService';
import { useSupervisor } from '@/hooks/useSupervisor';
import type { SupervisorActorRef } from '@/services/supervisorService';
import SetPrintSizeWindow from './SetPrintSizeWindow';
import {
  computePrintSizeChanges,
  normalizePrintSizeValue,
  type PrintSizeValue,
} from './utils/printSizeForm';
import SetPaperTypeWindow from './SetPaperTypeWindow';
import SetPrinterWindow from './SetPrinterWindow';
import { useOrderAiPrompts, type MissingField } from './useSelectionHelpPrompt';
import { selectMeasurementUnit } from '@/features/preferences/preferencesSlice';
import { convertInchesToUnit, unitPrecision } from '@/utils/measurement';
import { translateMeasurementUnitLabel } from '@/types/measurement';
import { Button } from '@/components/ui/button';
import ItemSelectionHeader, { type ItemSelectionOption } from './ItemSelectionHeader';
import { OrderItemQuantityField } from './OrderItemQuantityField';
import { getPaperTypes, getPrinters, type PaperType, type Printer } from '@/services/productService';
import { autoAssignSinglePrinter } from '@/components/order/utils/autoAssignSinglePrinter';
import { calculateOrderItemPricing } from '@/services/pricingService';
import { OrderImagePreview } from './OrderSummary';
import { getPrintQualityWarningLabel } from './utils/printQualityWarning';
import ConfirmAlertDialog from '@/components/ui/confirm-alert-dialog';
import { isOrderFlowItemStatus } from '@/types/orderStatus';

interface UploadedImagesGridProps {
  order: Order;
  onUpdatePrintSize?: (itemId: string, size: PrintSizeValue) => void;
  onUpdatePaperType?: (itemId: string, paperTypeId: string) => void;
  onUpdatePrinter?: (itemId: string, printerId: string) => void;
}

type MenuAction =
  | 'addToOrder'
  | 'removeFromOrder'
  | 'setPrintSize'
  | 'selectPaper'
  | 'selectPrinter'
  | 'selectFrame'
  | 'deleteImage';

interface PendingDeletion {
  readonly orderItemId: string;
  readonly label: string;
  readonly expiresAt: number;
}

const DELETION_GRACE_PERIOD_MS = 10_000;
const PREVIEW_PRICE_DEBOUNCE_MS = 1_000;
const DEFAULT_GALLERY_WRAP_IN = 2;
const CANVAS_NAME_MATCHER = /canvas/i;
const MIN_ITEM_QUANTITY = 1;
const MAX_ITEM_QUANTITY = 99;

const isOrderedStatus = (value: string | undefined | null): boolean => isOrderFlowItemStatus(value);

const clampItemQuantity = (value: number): number => Math.min(MAX_ITEM_QUANTITY, Math.max(MIN_ITEM_QUANTITY, Math.round(value)));

const resolveItemQuantity = (value: unknown): number => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < MIN_ITEM_QUANTITY) {
    return MIN_ITEM_QUANTITY;
  }
  return clampItemQuantity(numeric);
};

type ItemDialogEntry = {
  lookupKey: string;
  orderItemId: string;
  displayIndex: number;
  uploadedFileId: string | null;
  priceLabel: string | null;
  initialPaperTypeId: string | null;
  initialPrinterId: string | null;
  initialWidthIn: number | null;
  initialHeightIn: number | null;
  initialMarginIn: number | null;
  initialGalleryWrapIn: number | null;
  initialGalleryWrapMirrorEdges: boolean | null;
};

type SelectionEntry = { item: Order['items'][number]; index: number };

const dedupeSelectionEntries = (entries: readonly SelectionEntry[]): SelectionEntry[] => {
  const seen = new Set<string>();
  const result: SelectionEntry[] = [];
  entries.forEach(entry => {
    const identifier = entry.item.id;
    if (!identifier || seen.has(identifier)) {
      return;
    }
    seen.add(identifier);
    result.push(entry);
  });
  return result;
};

const isActivePaper = (paper: PaperType): boolean => {
  if (typeof (paper as PaperType & { available?: boolean }).available === 'boolean') {
    return Boolean((paper as PaperType & { available?: boolean }).available);
  }
  return true;
};

const formatPaperTypeLabel = (paper: PaperType): string => {
  const name = paper.name?.trim();
  if (name) {
    return name;
  }
  const category = paper.category?.trim();
  if (category) {
    return category;
  }
  return paper.brand?.trim() ?? 'Paper';
};

const formatPrinterLabel = (printer: Printer): string => {
  const name = printer.name?.trim();
  if (name) {
    return name;
  }
  const model = printer.model?.trim();
  if (model) {
    return model;
  }
  return printer.brand?.trim() ?? 'Printer';
};

const isCanvasPaperType = (paper: PaperType | null | undefined): boolean => {
  if (!paper) {
    return false;
  }
  const mediaType = paper.mediaType?.trim().toUpperCase();
  if (mediaType === 'CANVAS') {
    return true;
  }
  return CANVAS_NAME_MATCHER.test(paper.name ?? '');
};

const buildCanvasWrapPromptKey = (orderItemId: string, paperTypeId: string): string => `${orderItemId}:${paperTypeId}`;

type DialogState =
  | {
      kind: 'printSize';
      lookupKey: string;
      originOrderItemId: string;
      orderItemId?: string;
      entries: ItemDialogEntry[];
      draftSizes: Record<string, PrintSizeValue | null>;
      invalidOrderItemIds: Record<string, boolean>;
    }
  | {
      kind: 'paper';
      lookupKey: string;
      originOrderItemId: string;
      orderItemId: string;
      entries: ItemDialogEntry[];
      selections: Record<string, string | null>;
    }
  | {
      kind: 'printer';
      lookupKey: string;
      originOrderItemId: string;
      orderItemId: string;
      entries: ItemDialogEntry[];
      selections: Record<string, string | null>;
    };

type CanvasWrapPromptState = {
  readonly affectedItemIds: ReadonlyArray<string>;
  readonly declinedPromptKeys: ReadonlyArray<string>;
  readonly reopenEntries: ReadonlyArray<ItemDialogEntry>;
  readonly originOrderItemId: string;
};

type PrintSizeDiscardPromptState =
  | { readonly source: 'back' }
  | { readonly source: 'stepRibbon'; readonly targetStep: OrderStep };

type SelectionDialogKind = 'paper' | 'printer';

type SelectionDiscardPromptState =
  | { readonly kind: SelectionDialogKind; readonly source: 'back' }
  | { readonly kind: SelectionDialogKind; readonly source: 'stepRibbon'; readonly targetStep: OrderStep };

export const UploadedImagesGrid: React.FC<UploadedImagesGridProps> = ({
  order,
  onUpdatePrintSize,
  onUpdatePaperType,
  onUpdatePrinter,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selectedItemIds = useAppSelector(selectSelectedItemIds);
  const orderItems = useAppSelector(selectOrderItems) || [];
  const currentOrderId = useAppSelector((state) => state.order.id ?? undefined);
  const measurementUnit = useAppSelector(selectMeasurementUnit);
  const precision = unitPrecision(measurementUnit);
  const unitLabel = useMemo(() => translateMeasurementUnitLabel(t, measurementUnit), [measurementUnit, t]);
  const { send: sendToSupervisor } = useSupervisor();
  const sendSupervisorEvent = useCallback(
    (event: SupervisorEvent) => {
      (sendToSupervisor as SupervisorActorRef['send'])(event);
    },
    [sendToSupervisor],
  );
  const { sendSelectionHelpPrompt, sendMissingConfigurationPrompt } = useOrderAiPrompts(sendToSupervisor);
  const supervisorItems = Array.isArray(order.items) ? order.items : [];
  const [pendingDeletions, setPendingDeletions] = useState<PendingDeletion[]>([]);
  const deletionTimersRef = React.useRef<Map<string, number>>(new Map());
  const finalizeDeletionRef = React.useRef<(orderItemId: string) => void>();
  const [deletingIds, setDeletingIds] = useState<string[]>([]);
  const [hiddenItemIds, setHiddenItemIds] = useState<string[]>([]);
  const [now, setNow] = useState(() => Date.now());
  const [dialogState, setDialogState] = useState<DialogState | null>(null);
  const [paperLookup, setPaperLookup] = useState<Record<string, string>>({});
  const [paperCatalog, setPaperCatalog] = useState<Record<string, PaperType>>({});
  const [printerLookup, setPrinterLookup] = useState<Record<string, string>>({});
  const [isPaperLookupLoaded, setIsPaperLookupLoaded] = useState(false);
  const [isPrinterLookupLoaded, setIsPrinterLookupLoaded] = useState(false);
  const [dismissedCanvasWrapPromptKeys, setDismissedCanvasWrapPromptKeys] = useState<Set<string>>(() => new Set());
  const [canvasWrapPromptState, setCanvasWrapPromptState] = useState<CanvasWrapPromptState | null>(null);
  const [printSizeDiscardPromptState, setPrintSizeDiscardPromptState] = useState<PrintSizeDiscardPromptState | null>(null);
  const [selectionDiscardPromptState, setSelectionDiscardPromptState] = useState<SelectionDiscardPromptState | null>(null);
  const [quantityDrafts, setQuantityDrafts] = useState<Record<string, string>>({});
  type PreviewPriceStatus = 'idle' | 'loading' | 'success' | 'failed';
  type PreviewPriceEntry = {
    label: string | null;
    status: PreviewPriceStatus;
    value?: number | null;
    currency?: string | null;
  };

  const [previewPriceState, setPreviewPriceState] = useState<Record<string, PreviewPriceEntry>>({});
  const pricingCacheVersion = useAppSelector(selectOrderPricingCacheVersion);
  const previewRequestIdRef = React.useRef<Map<string, number>>(new Map());
  const pendingPrintSizeLeaveResolverRef = React.useRef<((allowed: boolean) => void) | null>(null);
  const pendingSelectionLeaveResolverRef = React.useRef<((allowed: boolean) => void) | null>(null);
  const dialogStateRef = React.useRef<DialogState | null>(null);
  const previewDebounceTimersRef = React.useRef<Map<string, number>>(new Map());
  const setDialogStateWithRef = useCallback((nextStateOrUpdater: React.SetStateAction<DialogState | null>) => {
    setDialogState((previousState) => {
      const nextState = typeof nextStateOrUpdater === 'function'
        ? (nextStateOrUpdater as (currentState: DialogState | null) => DialogState | null)(previousState)
        : nextStateOrUpdater;
      dialogStateRef.current = nextState;
      return nextState;
    });
  }, []);

  useEffect(() => {
    dialogStateRef.current = dialogState;
  }, [dialogState]);

  const computeHasUnsavedSelectionChanges = useCallback((state: DialogState | null, kind: SelectionDialogKind): boolean => {
    if (!state || state.kind !== kind) {
      return false;
    }

    const comparisons = state.entries.map((entry) => {
      const selection = state.selections[entry.orderItemId] ?? null;
      const initialSelection = kind === 'paper'
        ? (entry.initialPaperTypeId ?? null)
        : (entry.initialPrinterId ?? null);
      return {
        orderItemId: entry.orderItemId,
        initialSelection,
        selection,
        changed: initialSelection !== selection,
      };
    });
    const hasChanges = comparisons.some((entry) => entry.changed);

    return hasChanges;
  }, []);

  const canonicalItems = useMemo(() => {
    const merged: Order['items'] = [];
    const supervisorById = new Map<string, Order['items'][number]>();

    supervisorItems.forEach((item) => {
      if (item?.id) {
        supervisorById.set(item.id, item);
      }
    });

    orderItems.forEach((item) => {
      if (!item?.id) {
        return;
      }

      const fallback = supervisorById.get(item.id);
      merged.push({
        ...(fallback ?? {}),
        ...item,
        id: item.id,
        status: item.status ?? fallback?.status ?? 'NEW',
        orderId: item.orderId ?? fallback?.orderId ?? null,
        uploadedFileId: item.uploadedFileId ?? fallback?.uploadedFileId,
        createdAt: typeof item.createdAt === 'string' ? item.createdAt : fallback?.createdAt,
        updatedAt: typeof item.updatedAt === 'string' ? item.updatedAt : fallback?.updatedAt,
      });
      supervisorById.delete(item.id);
    });

    supervisorById.forEach((item) => {
      merged.push(item);
    });

    return merged;
  }, [orderItems, supervisorItems]);
  const latestCanonicalItemsRef = React.useRef(canonicalItems);
  const findStoreItem = useCallback((orderItemId: string | null | undefined) => {
    if (!orderItemId) {
      return null;
    }
    return orderItems.find(current => current?.id === orderItemId) ?? null;
  }, [orderItems]);
  const addToOrderLabel = useMemo(
    () => t('uploadedImages.actions.addToOrder', 'Add to order'),
    [t],
  );
  const removeFromOrderLabel = useMemo(
    () => t('uploadedImages.actions.removeFromOrder', 'Remove from order'),
    [t],
  );
  const inOrderLabel = useMemo(
    () => t('uploadedImages.actions.inOrder', 'In order'),
    [t],
  );
  const buildMissingFields = useCallback((item: Order['items'][number]) => {
    const storeItem = item.id
      ? orderItems.find(current => current?.id === item.id) ?? null
      : null;
    const resolvedWidthIn = storeItem?.widthIn ?? item.widthIn;
    const resolvedHeightIn = storeItem?.heightIn ?? item.heightIn;
    const resolvedPaperId = storeItem?.paperTypeId ?? item.paperTypeId;
    const resolvedPrinterId = storeItem?.printerId ?? item.printerId;

    const missing: MissingField[] = [];
    if (!(typeof resolvedWidthIn === 'number' && resolvedWidthIn > 0 && typeof resolvedHeightIn === 'number' && resolvedHeightIn > 0)) {
      missing.push('size');
    }
    if (!(typeof resolvedPaperId === 'string' && resolvedPaperId.length > 0)) {
      missing.push('paper');
    }
    if (!(typeof resolvedPrinterId === 'string' && resolvedPrinterId.length > 0)) {
      missing.push('printer');
    }
    return missing;
  }, [orderItems]);
  const staticMenuItems = useMemo(() => {
    const items: Array<{
      key: MenuAction;
      label: string;
      requiresPaper?: boolean;
      requiresPrinter?: boolean;
    }> = [
      { key: 'setPrintSize', label: t('uploadedImages.actions.setPrintSize', 'Set print size') },
      { key: 'selectPaper', label: t('uploadedImages.actions.selectPaper', 'Select paper'), requiresPaper: true },
      { key: 'selectPrinter', label: t('uploadedImages.actions.selectPrinter', 'Select printer'), requiresPrinter: true },
      { key: 'selectFrame', label: t('uploadedImages.actions.selectFrame', 'Select frame') },
      { key: 'deleteImage', label: t('uploadedImages.actions.deleteImage', 'Delete image') },
    ];
    return items
      .filter(item => {
        if (item.requiresPaper && !onUpdatePaperType) {
          return false;
        }
        if (item.requiresPrinter && !onUpdatePrinter) {
          return false;
        }
        return true;
      })
      .map(({ requiresPaper: _ignoredPaper, requiresPrinter: _ignoredPrinter, ...rest }) => rest);
  }, [onUpdatePaperType, onUpdatePrinter, t]);
  const getEffectiveStatus = useCallback((item: Order['items'][number]): string | undefined => {
    const storeItem = item.id
      ? orderItems.find(current => current?.id === item.id) ?? null
      : null;
    return (storeItem?.status ?? item.status) ?? undefined;
  }, [orderItems]);
  const getItemMenuActions = useCallback((item: Order['items'][number]) => {
    const effectiveStatus = getEffectiveStatus(item);
    const isOrdered = isOrderedStatus(effectiveStatus);
    const orderAction: { key: MenuAction; label: string } = isOrdered
      ? { key: 'removeFromOrder', label: removeFromOrderLabel }
      : { key: 'addToOrder', label: addToOrderLabel };
    const staticItems = staticMenuItems.slice();
    const deleteIndex = staticItems.findIndex(entry => entry.key === 'deleteImage');
    if (deleteIndex !== -1) {
      staticItems.splice(deleteIndex, 0, orderAction);
      return staticItems;
    }
    return [...staticItems, orderAction];
  }, [addToOrderLabel, getEffectiveStatus, removeFromOrderLabel, staticMenuItems]);
  const controlButtons = [
    { key: 'printSize', label: t('orderSummary.printSize', 'Print size') },
    { key: 'paper', label: t('orderSummary.paper', 'Paper') },
    { key: 'printer', label: t('orderSummary.printer', 'Printer') },
    { key: 'frame', label: t('orderSummary.frame', 'Frame') },
  ] as const;

  useEffect(() => {
    let cancelled = false;
    if (isPaperLookupLoaded) {
      return () => {
        cancelled = true;
      };
    }

    const fetchPaperTypes = async () => {
      try {
        const fetched = await getPaperTypes();
        if (cancelled) {
          return;
        }
        const active = fetched.filter(isActivePaper);
        const next: Record<string, string> = {};
        const nextCatalog: Record<string, PaperType> = {};
        active.forEach((paper) => {
          next[paper.id] = formatPaperTypeLabel(paper);
          nextCatalog[paper.id] = paper;
        });
        setPaperLookup(next);
        setPaperCatalog(nextCatalog);
      } catch (error) {
        if (!cancelled) {
          logging.error('[UploadedImagesGrid] Failed to load paper types', { error });
        }
      } finally {
        if (!cancelled) {
          setIsPaperLookupLoaded(true);
        }
      }
    };

    void fetchPaperTypes();

    return () => {
      cancelled = true;
    };
  }, [isPaperLookupLoaded]);

  useEffect(() => {
    let cancelled = false;
    if (isPrinterLookupLoaded) {
      return () => {
        cancelled = true;
      };
    }

    const fetchPrinters = async () => {
      try {
        const fetched = await getPrinters();
        if (cancelled) {
          return;
        }
        const next: Record<string, string> = {};
        fetched.forEach((printer: Printer) => {
          next[printer.id] = formatPrinterLabel(printer);
        });
        setPrinterLookup(next);
      } catch (error) {
        if (!cancelled) {
          logging.error('[UploadedImagesGrid] Failed to load printers', { error });
        }
      } finally {
        if (!cancelled) {
          setIsPrinterLookupLoaded(true);
        }
      }
    };

    void fetchPrinters();

    return () => {
      cancelled = true;
    };
  }, [isPrinterLookupLoaded]);


  const formatPriceLabel = useCallback((orderItemId: string | null | undefined) => {
    if (!orderItemId) {
      return null;
    }
    const storeItem = findStoreItem(orderItemId);
    if (!storeItem) {
      return null;
    }

    const pricingStatus = typeof storeItem.pricingStatus === 'string' ? storeItem.pricingStatus.toLowerCase() : undefined;
    if (pricingStatus === 'loading') {
      return t('setPrintSize.priceRibbon.loading', 'Calculating…');
    }
    if (pricingStatus === 'failed') {
      return t('setPrintSize.priceRibbon.error', 'Unavailable');
    }
    if (storeItem.price) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: storeItem.price.currency,
        }).format(storeItem.price.value);
      } catch (error) {
        logging.warn('[UploadedImagesGrid] Failed to format price', { error });
        return `${storeItem.price.value}`;
      }
    }
    return t('setPrintSize.priceRibbon.missing', 'TBD');
  }, [findStoreItem, t]);

  const resolvePreviewPriceLabel = useCallback((orderItemId: string | null | undefined) => {
    if (!orderItemId) {
      return null;
    }

    const preview = previewPriceState[orderItemId];
    if (preview) {
      if (preview.status === 'loading') {
        return t('setPrintSize.priceRibbon.loading', 'Calculating…');
      }
      if (preview.status === 'failed') {
        return preview.label ?? t('setPrintSize.priceRibbon.error', 'Unavailable');
      }
      if (preview.label != null) {
        return preview.label;
      }
    }

    return formatPriceLabel(orderItemId);
  }, [formatPriceLabel, previewPriceState, t]);

  const updatePreviewState = useCallback((orderItemId: string, next: PreviewPriceEntry | null) => {
    setPreviewPriceState(prev => {
      if (!next) {
        if (!(orderItemId in prev)) {
          return prev;
        }
        const { [orderItemId]: _removed, ...rest } = prev;
        return rest;
      }
      const current = prev[orderItemId];
      if (
        current
        && current.label === next.label
        && current.status === next.status
        && current.value === next.value
        && current.currency === next.currency
      ) {
        return prev;
      }
      return {
        ...prev,
        [orderItemId]: next,
      };
    });
  }, []);

  const clearPreviewDebounce = useCallback((orderItemId: string) => {
    const timerId = previewDebounceTimersRef.current.get(orderItemId);
    if (timerId) {
      window.clearTimeout(timerId);
      previewDebounceTimersRef.current.delete(orderItemId);
    }
  }, []);

  useEffect(() => {
    return () => {
      previewDebounceTimersRef.current.forEach(timerId => window.clearTimeout(timerId));
      previewDebounceTimersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    previewDebounceTimersRef.current.forEach(timerId => window.clearTimeout(timerId));
    previewDebounceTimersRef.current.clear();
    previewRequestIdRef.current.clear();
    setPreviewPriceState({});
  }, [pricingCacheVersion]);

  const queuePreviewPrice = useCallback((
    entry: ItemDialogEntry,
    options?: {
      paperTypeId?: string | null;
      printerId?: string | null;
      widthInches?: number | null;
      heightInches?: number | null;
      galleryWrapInches?: number | null;
    },
  ) => {
    const orderItemId = entry.orderItemId;
    const requestId = (previewRequestIdRef.current.get(orderItemId) ?? 0) + 1;
    previewRequestIdRef.current.set(orderItemId, requestId);
    clearPreviewDebounce(orderItemId);

    updatePreviewState(orderItemId, {
      label: null,
      status: 'loading',
    });

    const timerId = window.setTimeout(async () => {
      previewDebounceTimersRef.current.delete(orderItemId);
      if (previewRequestIdRef.current.get(orderItemId) !== requestId) {
        return;
      }

      const { paperTypeId, printerId, widthInches, heightInches, galleryWrapInches } = options ?? {};
      const payload: Parameters<typeof calculateOrderItemPricing>[0] = {
        uploadedFileId: entry.uploadedFileId ?? entry.orderItemId,
      };
      if (paperTypeId) {
        payload.paperTypeId = paperTypeId;
      }
      if (printerId) {
        payload.printerId = printerId;
      }
      if (typeof widthInches === 'number' && widthInches > 0) {
        payload.widthInches = widthInches;
      }
      if (typeof heightInches === 'number' && heightInches > 0) {
        payload.heightInches = heightInches;
      }
      if (typeof galleryWrapInches === 'number' && galleryWrapInches > 0) {
        payload.galleryWrapInches = galleryWrapInches;
      }

      try {
        const result = await calculateOrderItemPricing(payload);

        if (previewRequestIdRef.current.get(orderItemId) !== requestId) {
          return;
        }

        const priceValue = typeof result?.price?.value === 'number' ? result.price.value : null;
        const priceCurrency = typeof result?.price?.currency === 'string' ? result.price.currency : null;

        let previewLabel = formatPriceLabel(entry.orderItemId);
        if (priceValue != null && priceCurrency) {
          try {
            previewLabel = new Intl.NumberFormat(undefined, {
              style: 'currency',
              currency: priceCurrency,
            }).format(priceValue);
          } catch (error) {
            previewLabel = `${priceValue}`;
          }
        }

        updatePreviewState(orderItemId, {
          label: previewLabel,
          status: 'success',
          value: priceValue,
          currency: priceCurrency,
        });
      } catch (error) {
        if (previewRequestIdRef.current.get(orderItemId) !== requestId) {
          return;
        }

        logging.error('[UploadedImagesGrid] Failed to preview price', { error });
        updatePreviewState(orderItemId, {
          label: formatPriceLabel(entry.orderItemId),
          status: 'failed',
        });
      }
    }, PREVIEW_PRICE_DEBOUNCE_MS);

    previewDebounceTimersRef.current.set(orderItemId, timerId);
  }, [clearPreviewDebounce, formatPriceLabel, updatePreviewState]);

  const resolvePaperLabel = useCallback((paperTypeId?: string | null) => {
    if (!paperTypeId) {
      return null;
    }
    return paperLookup[paperTypeId] ?? null;
  }, [paperLookup]);

  const resolvePrinterLabel = useCallback((printerId?: string | null) => {
    if (!printerId) {
      return null;
    }
    return printerLookup[printerId] ?? null;
  }, [printerLookup]);

  const selectedItem = useMemo(() => {
    if (!dialogState) {
      return null;
    }
    return canonicalItems.find(item => item.id === dialogState.lookupKey) ?? null;
  }, [canonicalItems, dialogState]);

  const closeDialog = useCallback(() => {
    setDialogState(null);
  }, []);

  const openPrintSizeDialog = useCallback((entries: readonly ItemDialogEntry[], originOrderItemId: string) => {
    if (!onUpdatePrintSize || entries.length === 0 || !originOrderItemId) {
      return;
    }

    const initialDrafts: Record<string, PrintSizeValue> = {};
    entries.forEach((entry) => {
      const storeItem = findStoreItem(entry.orderItemId);
      const normalizedSize = normalizePrintSizeValue({
        widthIn: typeof storeItem?.widthIn === 'number'
          ? storeItem.widthIn
          : entry.initialWidthIn ?? undefined,
        heightIn: typeof storeItem?.heightIn === 'number'
          ? storeItem.heightIn
          : entry.initialHeightIn ?? undefined,
        marginIn: typeof storeItem?.marginIn === 'number'
          ? storeItem.marginIn
          : entry.initialMarginIn ?? undefined,
        galleryWrapIn: typeof storeItem?.galleryWrapIn === 'number'
          ? storeItem.galleryWrapIn
          : entry.initialGalleryWrapIn ?? undefined,
        galleryWrapMirrorEdges: typeof storeItem?.galleryWrapMirrorEdges === 'boolean'
          ? storeItem.galleryWrapMirrorEdges
          : entry.initialGalleryWrapMirrorEdges ?? undefined,
      });
      if (normalizedSize) {
        initialDrafts[entry.orderItemId] = normalizedSize;
      }
    });

    setDialogState({
      kind: 'printSize',
      lookupKey: originOrderItemId,
      originOrderItemId,
      orderItemId: originOrderItemId,
      entries: [...entries],
      draftSizes: initialDrafts,
      invalidOrderItemIds: {},
    });
  }, [findStoreItem, onUpdatePrintSize]);

  const buildPrintSizeDialogEntries = useCallback((targets: Array<{ item: Order['items'][number]; index: number }>): ItemDialogEntry[] => {
    return targets.map(entry => ({
      lookupKey: entry.item.id ?? '',
      orderItemId: entry.item.id ?? '',
      displayIndex: entry.index,
      uploadedFileId: entry.item.uploadedFileId ?? null,
      priceLabel: formatPriceLabel(entry.item.id ?? null),
      initialPaperTypeId: entry.item.paperTypeId ?? null,
      initialPrinterId: entry.item.printerId ?? null,
      initialWidthIn: entry.item.widthIn ?? null,
      initialHeightIn: entry.item.heightIn ?? null,
      initialMarginIn: entry.item.marginIn ?? null,
      initialGalleryWrapIn: entry.item.galleryWrapIn ?? null,
      initialGalleryWrapMirrorEdges: entry.item.galleryWrapMirrorEdges ?? null,
    }));
  }, [formatPriceLabel]);

  const applyPrintSizeChanges = useCallback((entries: readonly ItemDialogEntry[], drafts: Record<string, PrintSizeValue | null | undefined>, fallbackActive?: { orderItemId: string; size: PrintSizeValue }) => {
    if (!onUpdatePrintSize) {
      return;
    }

    const changes = computePrintSizeChanges({
      entries: entries.map(entry => ({
        orderItemId: entry.orderItemId,
        initialWidthIn: entry.initialWidthIn,
        initialHeightIn: entry.initialHeightIn,
        initialMarginIn: entry.initialMarginIn,
        initialGalleryWrapIn: entry.initialGalleryWrapIn,
        initialGalleryWrapMirrorEdges: entry.initialGalleryWrapMirrorEdges,
      })),
      drafts,
      active: fallbackActive,
      resolveCurrentSize: (orderItemId) => {
        const storeItem = findStoreItem(orderItemId);
        if (!storeItem) {
          return null;
        }
        return {
          widthIn: typeof storeItem.widthIn === 'number' ? storeItem.widthIn : null,
          heightIn: typeof storeItem.heightIn === 'number' ? storeItem.heightIn : null,
          marginIn: typeof storeItem.marginIn === 'number' ? storeItem.marginIn : null,
          galleryWrapIn: typeof storeItem.galleryWrapIn === 'number' ? storeItem.galleryWrapIn : null,
          galleryWrapMirrorEdges: typeof storeItem.galleryWrapMirrorEdges === 'boolean' ? storeItem.galleryWrapMirrorEdges : null,
        };
      },
    });

    if (changes.length === 0) {
      if (!fallbackActive) {
        return;
      }

      const storeItem = findStoreItem(fallbackActive.orderItemId);
      const needsForcedReprice = !storeItem?.price && storeItem?.pricingStatus === 'idle';
      if (!needsForcedReprice) {
        return;
      }

      onUpdatePrintSize(fallbackActive.orderItemId, fallbackActive.size);
      updatePreviewState(fallbackActive.orderItemId, null);
      return;
    }

    changes.forEach(({ orderItemId, size }) => {
      onUpdatePrintSize(orderItemId, size);
      updatePreviewState(orderItemId, null);
    });
  }, [findStoreItem, onUpdatePrintSize, updatePreviewState]);

  const getPendingPrintSizeChanges = useCallback((state: Extract<DialogState, { kind: 'printSize' }>) => {
    return computePrintSizeChanges({
      entries: state.entries.map(entry => ({
        orderItemId: entry.orderItemId,
        initialWidthIn: entry.initialWidthIn,
        initialHeightIn: entry.initialHeightIn,
        initialMarginIn: entry.initialMarginIn,
        initialGalleryWrapIn: entry.initialGalleryWrapIn,
        initialGalleryWrapMirrorEdges: entry.initialGalleryWrapMirrorEdges,
      })),
      drafts: state.draftSizes ?? {},
      resolveCurrentSize: (orderItemId) => {
        const storeItem = findStoreItem(orderItemId);
        if (!storeItem) {
          return null;
        }
        return {
          widthIn: typeof storeItem.widthIn === 'number' ? storeItem.widthIn : null,
          heightIn: typeof storeItem.heightIn === 'number' ? storeItem.heightIn : null,
          marginIn: typeof storeItem.marginIn === 'number' ? storeItem.marginIn : null,
          galleryWrapIn: typeof storeItem.galleryWrapIn === 'number' ? storeItem.galleryWrapIn : null,
          galleryWrapMirrorEdges: typeof storeItem.galleryWrapMirrorEdges === 'boolean' ? storeItem.galleryWrapMirrorEdges : null,
        };
      },
    });
  }, [findStoreItem]);

  const pendingPrintSizeChanges = useMemo(() => {
    if (!dialogState || dialogState.kind !== 'printSize') {
      return [];
    }

    return getPendingPrintSizeChanges(dialogState);
  }, [dialogState, getPendingPrintSizeChanges]);

  const hasUnsavedPrintSizeChanges = pendingPrintSizeChanges.length > 0;

  const hasUnsavedPaperChanges = computeHasUnsavedSelectionChanges(dialogState, 'paper');
  const hasUnsavedPrinterChanges = computeHasUnsavedSelectionChanges(dialogState, 'printer');

  const resolvePendingPrintSizeLeave = useCallback((allowed: boolean) => {
    const resolver = pendingPrintSizeLeaveResolverRef.current;
    pendingPrintSizeLeaveResolverRef.current = null;
    if (resolver) {
      resolver(allowed);
    }
  }, []);

  const resolvePendingSelectionLeave = useCallback((allowed: boolean) => {
    const resolver = pendingSelectionLeaveResolverRef.current;
    pendingSelectionLeaveResolverRef.current = null;
    if (resolver) {
      resolver(allowed);
    }
  }, []);

  const handlePrintSizeBackRequest = useCallback(() => {
    if (!dialogState || dialogState.kind !== 'printSize' || !hasUnsavedPrintSizeChanges) {
      closeDialog();
      return;
    }

    setPrintSizeDiscardPromptState({ source: 'back' });
  }, [closeDialog, dialogState, hasUnsavedPrintSizeChanges]);

  const handlePrintSizeDiscardPromptOpenChange = useCallback((open: boolean) => {
    if (open) {
      return;
    }

    if (printSizeDiscardPromptState?.source === 'stepRibbon') {
      resolvePendingPrintSizeLeave(false);
    }
    setPrintSizeDiscardPromptState(null);
  }, [printSizeDiscardPromptState, resolvePendingPrintSizeLeave]);

  const handleConfirmPrintSizeDiscard = useCallback(() => {
    const promptState = printSizeDiscardPromptState;
    setPrintSizeDiscardPromptState(null);
    closeDialog();

    if (promptState?.source === 'stepRibbon') {
      resolvePendingPrintSizeLeave(true);
    }
  }, [closeDialog, printSizeDiscardPromptState, resolvePendingPrintSizeLeave]);

  useEffect(() => {
    if (!dialogState || dialogState.kind !== 'printSize') {
      return undefined;
    }

    return registerStepLeaveGuard(({ source, targetStep }) => {
      if (source !== 'stepRibbon') {
        return true;
      }

      if (!hasUnsavedPrintSizeChanges) {
        return true;
      }

      return new Promise<boolean>((resolve) => {
        pendingPrintSizeLeaveResolverRef.current = resolve;
        setPrintSizeDiscardPromptState({
          source: 'stepRibbon',
          targetStep,
        });
      });
    });
  }, [dialogState, hasUnsavedPrintSizeChanges]);

  useEffect(() => () => {
    resolvePendingPrintSizeLeave(false);
  }, [resolvePendingPrintSizeLeave]);

  const handleSelectionBackRequest = useCallback((kind: SelectionDialogKind) => {
    const state = dialogStateRef.current;
    const hasUnsavedChanges = computeHasUnsavedSelectionChanges(state, kind);
    if (!hasUnsavedChanges) {
      closeDialog();
      return;
    }

    setSelectionDiscardPromptState({ kind, source: 'back' });
  }, [closeDialog, computeHasUnsavedSelectionChanges]);

  const handleSelectionDiscardPromptOpenChange = useCallback((open: boolean) => {
    if (open) {
      return;
    }

    if (selectionDiscardPromptState?.source === 'stepRibbon') {
      resolvePendingSelectionLeave(false);
    }
    setSelectionDiscardPromptState(null);
  }, [resolvePendingSelectionLeave, selectionDiscardPromptState]);

  const handleConfirmSelectionDiscard = useCallback(() => {
    const promptState = selectionDiscardPromptState;
    setSelectionDiscardPromptState(null);
    closeDialog();

    if (promptState?.source === 'stepRibbon') {
      resolvePendingSelectionLeave(true);
    }
  }, [closeDialog, resolvePendingSelectionLeave, selectionDiscardPromptState]);

  useEffect(() => {
    if (!dialogState || (dialogState.kind !== 'paper' && dialogState.kind !== 'printer')) {
      return undefined;
    }

    return registerStepLeaveGuard(({ source, targetStep }) => {
      if (source !== 'stepRibbon') {
        return true;
      }

      const currentState = dialogStateRef.current;
      const hasUnsavedChanges = computeHasUnsavedSelectionChanges(currentState, dialogState.kind);

      if (!hasUnsavedChanges) {
        return true;
      }

      return new Promise<boolean>((resolve) => {
        pendingSelectionLeaveResolverRef.current = resolve;
        setSelectionDiscardPromptState({
          kind: dialogState.kind,
          source: 'stepRibbon',
          targetStep,
        });
      });
    });
  }, [dialogState, hasUnsavedPaperChanges, hasUnsavedPrinterChanges]);

  useEffect(() => () => {
    resolvePendingSelectionLeave(false);
  }, [resolvePendingSelectionLeave]);

  const handlePrintSizeSave = useCallback((size: PrintSizeValue) => {
    if (!dialogState || dialogState.kind !== 'printSize') {
      return;
    }
    if (Object.values(dialogState.invalidOrderItemIds ?? {}).some(Boolean)) {
      return;
    }

    const activeSize = normalizePrintSizeValue(size);
    applyPrintSizeChanges(dialogState.entries, dialogState.draftSizes ?? {}, dialogState.orderItemId && activeSize ? {
      orderItemId: dialogState.orderItemId,
      size: activeSize,
    } : undefined);

    closeDialog();
  }, [applyPrintSizeChanges, closeDialog, dialogState]);

  const handlePaperSelectionChange = useCallback((paperTypeId: string) => {
    setDialogStateWithRef(prev => {
      if (!prev || prev.kind !== 'paper') {
        return prev;
      }

      const entry = prev.entries.find(candidate => candidate.orderItemId === prev.orderItemId);
      const nextSelections = {
        ...prev.selections,
        [prev.orderItemId]: paperTypeId,
      } satisfies Extract<DialogState, { kind: 'paper' }>['selections'];

      if (entry) {
        const storeItem = findStoreItem(entry.orderItemId);
        const resolvedPrinter = storeItem?.printerId ?? entry.initialPrinterId ?? null;
        const widthInches = storeItem?.widthIn ?? null;
        const heightInches = storeItem?.heightIn ?? null;
        const galleryWrapInches = storeItem?.galleryWrapIn ?? null;
        if (!paperTypeId) {
          updatePreviewState(entry.orderItemId, null);
        } else {
          void queuePreviewPrice(entry, {
            paperTypeId,
            printerId: resolvedPrinter,
            widthInches,
            heightInches,
            galleryWrapInches,
          });
        }
      }

      return {
        ...prev,
        selections: nextSelections,
      } satisfies DialogState;
    });
  }, [findStoreItem, queuePreviewPrice, updatePreviewState]);

  const handlePaperTypeSave = useCallback(async (paperTypeId: string) => {
    if (!dialogState || dialogState.kind !== 'paper') {
      return;
    }
    if (!onUpdatePaperType) {
      return;
    }
    const selections: Record<string, string | null> = {
      ...dialogState.selections,
    };
    if (dialogState.orderItemId) {
      selections[dialogState.orderItemId] = paperTypeId;
    }

    const changedEntries: Array<{
      entry: ItemDialogEntry;
      paperTypeId: string;
    }> = [];

    dialogState.entries.forEach(entry => {
      const selection = selections[entry.orderItemId];
      if (!selection) {
        return;
      }
      const storeItem = findStoreItem(entry.orderItemId);
      const currentSelection = storeItem?.paperTypeId ?? entry.initialPaperTypeId;
      if (currentSelection === selection) {
        return;
      }
      changedEntries.push({ entry, paperTypeId: selection });
    });
    if (changedEntries.length > 0) {
      const changedIds: string[] = [];
      for (const { entry, paperTypeId: nextPaperTypeId } of changedEntries) {
        const preview = previewPriceState[entry.orderItemId];
        const previewValue = preview?.status === 'success' && typeof preview?.value === 'number'
          ? preview.value
          : undefined;
        const previewCurrency = preview?.status === 'success' && preview?.currency
          ? preview.currency
          : 'USD';

        dispatch(updateOrderItem({
          orderItemId: entry.orderItemId,
          data: {
            paperTypeId: nextPaperTypeId,
            ...(typeof previewValue === 'number'
              ? {
                  price: { value: previewValue, currency: previewCurrency },
                  pricingStatus: 'succeeded',
                  pricingError: null,
                }
              : {}),
          },
        }));
        changedIds.push(entry.orderItemId);
      }

      await dispatch(persistDirtyOrderItems({ itemIds: changedIds })).unwrap().catch(() => undefined);
      for (const { entry, paperTypeId: nextPaperTypeId } of changedEntries) {
        onUpdatePaperType(entry.orderItemId, nextPaperTypeId);
        updatePreviewState(entry.orderItemId, null);
      }
    }

    const nextDismissedCanvasWrapPromptKeys = new Set(dismissedCanvasWrapPromptKeys);
    changedEntries.forEach(({ entry }) => {
      Array.from(nextDismissedCanvasWrapPromptKeys).forEach((key) => {
        if (key.startsWith(`${entry.orderItemId}:`)) {
          nextDismissedCanvasWrapPromptKeys.delete(key);
        }
      });
    });
    if (nextDismissedCanvasWrapPromptKeys.size !== dismissedCanvasWrapPromptKeys.size) {
      setDismissedCanvasWrapPromptKeys(nextDismissedCanvasWrapPromptKeys);
    }

    const canvasPromptCandidates = changedEntries.flatMap(({ entry, paperTypeId: nextPaperTypeId }) => {
      const selectedPaper = paperCatalog[nextPaperTypeId] ?? null;
      if (!isCanvasPaperType(selectedPaper)) {
        return [];
      }

      const storeItem = findStoreItem(entry.orderItemId);
      const currentGalleryWrapIn = typeof storeItem?.galleryWrapIn === 'number'
        ? storeItem.galleryWrapIn
        : entry.initialGalleryWrapIn ?? null;
      if (typeof currentGalleryWrapIn === 'number' && currentGalleryWrapIn > 0) {
        return [];
      }

      const promptKey = buildCanvasWrapPromptKey(entry.orderItemId, nextPaperTypeId);
      if (nextDismissedCanvasWrapPromptKeys.has(promptKey)) {
        return [];
      }

      return [{
        orderItemId: entry.orderItemId,
        promptKey,
      }];
    });

    try {
      const entries = dialogState.entries ?? [];
      const eligibleIds = new Set(entries.map(entry => entry.orderItemId));
      const sourceItems = entries.map(entry => {
        const storeItem = findStoreItem(entry.orderItemId);
        return {
          id: entry.orderItemId,
          printerId: storeItem?.printerId ?? entry.initialPrinterId ?? null,
        };
      });

      await autoAssignSinglePrinter({
        getPrinters,
        eligibleIds,
        items: sourceItems,
        onAssign: async (idsToUpdate, singlePrinterId) => {
          idsToUpdate.forEach(orderItemId => {
            dispatch(updateOrderItem({
              orderItemId,
              data: { printerId: singlePrinterId },
            }));
          });

          await dispatch(persistDirtyOrderItems({ itemIds: idsToUpdate })).unwrap().catch(() => undefined);

          if (onUpdatePrinter) {
            idsToUpdate.forEach(orderItemId => {
              onUpdatePrinter(orderItemId, singlePrinterId);
            });
          }
        },
      });
    } catch (error) {
      logging.error('[UploadedImagesGrid] Failed to auto-assign single printer after paper save', { error });
    }

    if (canvasPromptCandidates.length > 0) {
      const affectedItemIds = canvasPromptCandidates.map(candidate => candidate.orderItemId);
      const declinedPromptKeys = canvasPromptCandidates.map(candidate => candidate.promptKey);
      setCanvasWrapPromptState({
        affectedItemIds,
        declinedPromptKeys,
        reopenEntries: dialogState.entries,
        originOrderItemId: dialogState.originOrderItemId,
      });
      sendSupervisorEvent({
        type: 'UI.REQUEST_AI_PROMPT',
        aiPrompt: `Explain briefly and clearly that canvas prints are often produced with gallery wrap so the image can continue around the stretcher bars. Tell the user we can add a default 2 inch gallery wrap to each selected canvas item now, replace any existing margin on those items, and then open the size settings so they can adjust it. Ask them whether they want to add gallery wrap.`,
        fallbackMessage: t(
          'uploadedImages.canvasWrapPrompt.aiFallback',
          'Canvas prints often need gallery wrap so the image can continue around the stretcher bars. We can add a default 2 inch gallery wrap to the selected canvas items now and open size settings so you can adjust it.',
        ),
      });
      return false;
    }

    closeDialog();
    return true;
  }, [
    closeDialog,
    dialogState,
    dispatch,
    dismissedCanvasWrapPromptKeys,
    findStoreItem,
    onUpdatePaperType,
    onUpdatePrinter,
    paperCatalog,
    previewPriceState,
    sendSupervisorEvent,
    t,
    updatePreviewState,
  ]);

  const handleCanvasWrapPromptOpenChange = useCallback((open: boolean) => {
    if (open || !canvasWrapPromptState) {
      return;
    }

    setDismissedCanvasWrapPromptKeys(prev => {
      const next = new Set(prev);
      canvasWrapPromptState.declinedPromptKeys.forEach((key) => {
        next.add(key);
      });
      return next;
    });
    setCanvasWrapPromptState(null);
    closeDialog();
  }, [canvasWrapPromptState, closeDialog]);

  const handleConfirmCanvasWrap = useCallback(async () => {
    if (!canvasWrapPromptState) {
      return;
    }

    const promptState = canvasWrapPromptState;
    setCanvasWrapPromptState(null);

    const fallbackPersistIds: string[] = [];
    promptState.affectedItemIds.forEach((orderItemId) => {
      const storeItem = findStoreItem(orderItemId);
      const resolvedWidthIn = typeof storeItem?.widthIn === 'number' ? storeItem.widthIn : null;
      const resolvedHeightIn = typeof storeItem?.heightIn === 'number' ? storeItem.heightIn : null;

      if (
        onUpdatePrintSize
        && typeof resolvedWidthIn === 'number'
        && resolvedWidthIn > 0
        && typeof resolvedHeightIn === 'number'
        && resolvedHeightIn > 0
      ) {
        onUpdatePrintSize(orderItemId, {
          widthIn: resolvedWidthIn,
          heightIn: resolvedHeightIn,
          galleryWrapIn: DEFAULT_GALLERY_WRAP_IN,
          galleryWrapMirrorEdges: false,
        });
      } else {
        dispatch(updateOrderItem({
          orderItemId,
          data: {
            marginIn: null,
            galleryWrapIn: DEFAULT_GALLERY_WRAP_IN,
            galleryWrapMirrorEdges: false,
          },
        }));
        fallbackPersistIds.push(orderItemId);
      }

      updatePreviewState(orderItemId, null);
    });

    if (fallbackPersistIds.length > 0) {
      await dispatch(persistDirtyOrderItems({ itemIds: fallbackPersistIds })).unwrap().catch(() => undefined);
    }

    closeDialog();
  }, [canvasWrapPromptState, closeDialog, dispatch, findStoreItem, onUpdatePrintSize, updatePreviewState]);

  const handleSelectionConfirm = useCallback(
    (
      kind: 'paper' | 'printer',
      value: string,
      saveFn: (nextValue: string) => void | boolean | Promise<void | boolean>,
    ) => {
      if (!dialogState || dialogState.kind !== kind) {
        return;
      }

      const entries = dialogState.entries ?? [];
      if (entries.length <= 1) {
        void saveFn(value);
        return;
      }

      const currentIndex = entries.findIndex(entry => entry.orderItemId === dialogState.orderItemId);
      const startingIndex = currentIndex >= 0 ? currentIndex : 0;
      const nextSelections: Record<string, string | null> = {
        ...dialogState.selections,
        [dialogState.orderItemId]: value,
      };

      const isAssigned = (entry: ItemDialogEntry): boolean => {
        const selection = nextSelections[entry.orderItemId];
        return typeof selection === 'string' && selection.length > 0;
      };

      const findNextUnassigned = (): ItemDialogEntry | null => {
        for (let i = startingIndex + 1; i < entries.length; i += 1) {
          if (!isAssigned(entries[i])) {
            return entries[i];
          }
        }
        for (let i = 0; i < startingIndex; i += 1) {
          if (!isAssigned(entries[i])) {
            return entries[i];
          }
        }
        return null;
      };

      const nextEntry = findNextUnassigned();
      if (!nextEntry) {
        void saveFn(value);
        return;
      }

      setDialogStateWithRef(prev => {
        if (!prev || prev.kind !== kind) {
          return prev;
        }
        return {
          ...prev,
          lookupKey: nextEntry.orderItemId,
          orderItemId: nextEntry.orderItemId,
          selections: nextSelections,
        } satisfies DialogState;
      });
    },
    [dialogState],
  );

  const handlePaperSelectionConfirm = useCallback(
    (paperTypeId: string) => {
      handleSelectionConfirm('paper', paperTypeId, handlePaperTypeSave);
    },
    [handlePaperTypeSave, handleSelectionConfirm],
  );

  const handleApplyPaperToAllItems = useCallback((paperTypeId: string) => {
    const entries = dialogState && dialogState.kind === 'paper' ? dialogState.entries ?? [] : [];
    if (entries.length === 0) {
      return;
    }

    setDialogStateWithRef(prev => {
      if (!prev || prev.kind !== 'paper') {
        return prev;
      }
      const nextSelections: Record<string, string | null> = { ...prev.selections };
      prev.entries.forEach(entry => {
        nextSelections[entry.orderItemId] = paperTypeId;
      });
      return {
        ...prev,
        selections: nextSelections,
      } satisfies DialogState;
    });

    entries.forEach(entry => {
      const storeItem = findStoreItem(entry.lookupKey);
      const resolvedPrinter = storeItem?.printerId ?? entry.initialPrinterId ?? null;
      void queuePreviewPrice(entry, {
        paperTypeId,
        printerId: resolvedPrinter,
        widthInches: storeItem?.widthIn ?? null,
        heightInches: storeItem?.heightIn ?? null,
        galleryWrapInches: storeItem?.galleryWrapIn ?? null,
      });
    });
  }, [dialogState, findStoreItem, queuePreviewPrice]);

  const handlePrinterSelectionChange = useCallback((printerId: string) => {
    setDialogStateWithRef(prev => {
      if (!prev || prev.kind !== 'printer') {
        return prev;
      }

      const entry = prev.entries.find(candidate => candidate.orderItemId === prev.orderItemId);
      const nextSelections = {
        ...prev.selections,
        [prev.orderItemId]: printerId,
      } satisfies Extract<DialogState, { kind: 'printer' }>['selections'];

      if (entry) {
        const storeItem = findStoreItem(entry.orderItemId);
        const resolvedPaper = storeItem?.paperTypeId ?? entry.initialPaperTypeId ?? null;
        const widthInches = storeItem?.widthIn ?? null;
        const heightInches = storeItem?.heightIn ?? null;
        const galleryWrapInches = storeItem?.galleryWrapIn ?? null;
        if (!printerId) {
          updatePreviewState(entry.orderItemId, null);
        } else {
          void queuePreviewPrice(entry, {
            paperTypeId: resolvedPaper,
            printerId,
            widthInches,
            heightInches,
            galleryWrapInches,
          });
        }
      }

      return {
        ...prev,
        selections: nextSelections,
      } satisfies DialogState;
    });
  }, [findStoreItem, queuePreviewPrice, updatePreviewState]);

  const handlePrinterSave = useCallback(async (printerId: string) => {
    if (!dialogState || dialogState.kind !== 'printer') {
      return;
    }
    if (!onUpdatePrinter) {
      return;
    }

    const selections: Record<string, string | null> = {
      ...dialogState.selections,
    };
    if (dialogState.orderItemId) {
      selections[dialogState.orderItemId] = printerId;
    }

    const changedEntries: Array<{
      entry: ItemDialogEntry;
      printerId: string;
    }> = [];

    dialogState.entries.forEach(entry => {
      const selection = selections[entry.orderItemId];
      if (!selection) {
        return;
      }
      const storeItem = findStoreItem(entry.lookupKey);
      const currentSelection = storeItem?.printerId ?? entry.initialPrinterId;
      if (currentSelection === selection) {
        return;
      }
      changedEntries.push({ entry, printerId: selection });
    });

    if (changedEntries.length > 0) {
      const changedIds: string[] = [];
      for (const { entry, printerId: nextPrinterId } of changedEntries) {
        const preview = previewPriceState[entry.orderItemId];
        const previewValue = preview?.status === 'success' && typeof preview?.value === 'number'
          ? preview.value
          : undefined;
        const previewCurrency = preview?.status === 'success' && preview?.currency
          ? preview.currency
          : 'USD';

        dispatch(updateOrderItem({
          orderItemId: entry.orderItemId,
          data: {
            printerId: nextPrinterId,
            ...(typeof previewValue === 'number'
              ? {
                  price: { value: previewValue, currency: previewCurrency },
                  pricingStatus: 'succeeded',
                  pricingError: null,
                }
              : {}),
          },
        }));
        changedIds.push(entry.orderItemId);
      }

      await dispatch(persistDirtyOrderItems({ itemIds: changedIds })).unwrap().catch(() => undefined);
      for (const { entry, printerId: nextPrinterId } of changedEntries) {
        onUpdatePrinter(entry.orderItemId, nextPrinterId);
        updatePreviewState(entry.orderItemId, null);
      }
    }
    closeDialog();
  }, [closeDialog, dialogState, dispatch, findStoreItem, onUpdatePrinter, previewPriceState, updatePreviewState]);

  const handlePrinterSelectionConfirm = useCallback(
    (printerId: string) => {
      handleSelectionConfirm('printer', printerId, handlePrinterSave);
    },
    [handlePrinterSave, handleSelectionConfirm],
  );

  const handleApplyPrinterToAllItems = useCallback((printerId: string) => {
    const entries = dialogState && dialogState.kind === 'printer' ? dialogState.entries ?? [] : [];
    if (entries.length === 0) {
      return;
    }

    setDialogStateWithRef(prev => {
      if (!prev || prev.kind !== 'printer') {
        return prev;
      }
      const nextSelections: Record<string, string | null> = { ...prev.selections };
      prev.entries.forEach(entry => {
        nextSelections[entry.orderItemId] = printerId;
      });
      return {
        ...prev,
        selections: nextSelections,
      } satisfies DialogState;
    });

    entries.forEach(entry => {
      const storeItem = findStoreItem(entry.lookupKey);
      const resolvedPaper = storeItem?.paperTypeId ?? entry.initialPaperTypeId ?? null;
      void queuePreviewPrice(entry, {
        paperTypeId: resolvedPaper,
        printerId,
        widthInches: storeItem?.widthIn ?? null,
        heightInches: storeItem?.heightIn ?? null,
        galleryWrapInches: storeItem?.galleryWrapIn ?? null,
      });
    });
  }, [dialogState, findStoreItem, queuePreviewPrice]);

  const handleSelectionChange = (itemId: string, selected: boolean) => {
    dispatch(setItemSelection({ itemId, selected }));
    analytics.track('checkbox: ui.uploaded_images_select_toggle', { itemId, selected });
  };

  const handleQuantityDraftChange = useCallback((orderItemId: string, rawValue: string) => {
    if (!/^\d{0,2}$/.test(rawValue)) {
      return;
    }

    setQuantityDrafts((prev) => ({
      ...prev,
      [orderItemId]: rawValue,
    }));

    if (rawValue === '') {
      return;
    }

    const parsed = Number(rawValue);
    if (!Number.isInteger(parsed) || parsed < MIN_ITEM_QUANTITY || parsed > MAX_ITEM_QUANTITY) {
      return;
    }

    dispatch(updateOrderItem({
      orderItemId,
      data: { quantity: parsed },
    }));
  }, [dispatch]);

  const handleQuantityCommit = useCallback(async (orderItemId: string) => {
    const storeItem = findStoreItem(orderItemId);
    const draft = quantityDrafts[orderItemId];
    const nextQuantity = draft === undefined || draft === ''
      ? resolveItemQuantity(storeItem?.quantity)
      : resolveItemQuantity(draft);

    setQuantityDrafts((prev) => {
      if (!(orderItemId in prev)) {
        return prev;
      }
      const { [orderItemId]: _removed, ...rest } = prev;
      return rest;
    });

    if (storeItem?.quantity !== nextQuantity) {
      dispatch(updateOrderItem({
        orderItemId,
        data: { quantity: nextQuantity },
      }));
    }

    await dispatch(persistDirtyOrderItems({ itemIds: [orderItemId] })).unwrap().catch(() => undefined);
  }, [dispatch, findStoreItem, quantityDrafts]);

  const clearDeletionTimer = useCallback((orderItemId: string) => {
    const timerId = deletionTimersRef.current.get(orderItemId);
    if (typeof timerId === 'number') {
      clearTimeout(timerId);
      deletionTimersRef.current.delete(orderItemId);
    }
  }, []);

  useEffect(() => {
    latestCanonicalItemsRef.current = canonicalItems;
  }, [canonicalItems]);

  const finalizeDeletion = useCallback(async (orderItemId: string) => {
    clearDeletionTimer(orderItemId);
    setDeletingIds(prev => (prev.includes(orderItemId) ? prev : [...prev, orderItemId]));
    try {
      analytics.track('timer: ui.uploaded_images_delete_confirm', { itemId: orderItemId });
      await deleteOrderItem(orderItemId);
      setPendingDeletions(prev => prev.filter(candidate => candidate.orderItemId !== orderItemId));
      dispatch(removeOrderItemById(orderItemId));

      setHiddenItemIds(prev => (prev.includes(orderItemId) ? prev : [...prev, orderItemId]));
      const currentItems = latestCanonicalItemsRef.current;
      if (Array.isArray(currentItems)) {
        const nextItems = currentItems.filter(item => item?.id !== orderItemId);
        sendSupervisorEvent({ type: 'UI.IMAGES_UPDATED' });
        sendSupervisorEvent({ type: 'ORDER.APPLY_PATCH', patch: { items: nextItems } });
      }
    } catch (error) {
      logging.error('[UploadedImagesGrid] Failed to delete order item', { error });
      setPendingDeletions(prev => prev.filter(current => current.orderItemId !== orderItemId));
      if (typeof window !== 'undefined') {
        const message = t(
          'uploadedImages.deleteFailed',
          'Unable to delete this item. It may already be part of an order.',
        );
        window.alert(message);
      }
    }
    setDeletingIds(prev => prev.filter(id => id !== orderItemId));
  }, [clearDeletionTimer, dispatch, pendingDeletions, sendToSupervisor, t]);

  useEffect(() => {
    finalizeDeletionRef.current = finalizeDeletion;
  }, [finalizeDeletion]);

  const beginPendingDeletion = useCallback((item: Order['items'][number], label: string) => {
    const orderItemId = item?.id;
    if (!orderItemId || deletionTimersRef.current.has(orderItemId)) {
      return;
    }
    if (pendingDeletions.some(entry => entry.orderItemId === orderItemId)) {
      return;
    }
    analytics.track('dropdown: ui.uploaded_images_delete_request', { itemId: orderItemId });
    const expiresAt = Date.now() + DELETION_GRACE_PERIOD_MS;
    const timeoutId = window.setTimeout(() => finalizeDeletionRef.current?.(orderItemId), DELETION_GRACE_PERIOD_MS);
    deletionTimersRef.current.set(orderItemId, timeoutId);
    setPendingDeletions(prev => [...prev, { orderItemId, label, expiresAt }]);
  }, [pendingDeletions]);

  const handleUndoDeletion = useCallback((orderItemId: string) => {
    clearDeletionTimer(orderItemId);
    setPendingDeletions(prev => prev.filter(entry => entry.orderItemId !== orderItemId));
    analytics.track('button: ui.uploaded_images_delete_undo', { itemId: orderItemId });
  }, [clearDeletionTimer]);

  const handleGoToUpload = useCallback(() => {
    sendSupervisorEvent({ type: 'UI.GOTO_STEP', step: 'upload' });
    analytics.track('button: ui.uploaded_images_go_to_upload');
  }, [sendToSupervisor]);

  useEffect(() => {
    return () => {
      deletionTimersRef.current.forEach(timerId => clearTimeout(timerId));
      deletionTimersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    if (pendingDeletions.length === 0) {
      return undefined;
    }
    const intervalId = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => {
      clearInterval(intervalId);
    };
  }, [pendingDeletions.length]);

  useEffect(() => {
    const validIds = new Set(canonicalItems.map(current => current.id).filter((id): id is string => Boolean(id)));

    setPendingDeletions(prev => {
      let changed = false;
      const next: PendingDeletion[] = [];
      prev.forEach(entry => {
        if (validIds.has(entry.orderItemId)) {
          next.push(entry);
        } else {
          changed = true;
          clearDeletionTimer(entry.orderItemId);
        }
      });
      return changed ? next : prev;
    });

    setDeletingIds(prev => {
      const next = prev.filter(id => validIds.has(id));
      return next.length === prev.length ? prev : next;
    });

    setHiddenItemIds(prev => {
      const next = prev.filter(id => validIds.has(id));
      return next.length === prev.length ? prev : next;
    });

  }, [canonicalItems, clearDeletionTimer]);

  const pendingMap = useMemo(() => new Map(pendingDeletions.map(entry => [entry.orderItemId, entry])), [pendingDeletions]);
  const hiddenSet = useMemo(() => new Set(hiddenItemIds), [hiddenItemIds]);
  const visibleItems = useMemo(() => {
    return canonicalItems.filter(item => {
      if (!item?.id) return true;
      return !hiddenSet.has(item.id);
    });
  }, [canonicalItems, hiddenSet]);

  const shouldShowSelection = useMemo(() => {
    if (visibleItems.length === 0) {
      return false;
    }
    if (visibleItems.length > 1) {
      return true;
    }

    const soleItem = visibleItems[0];
    const rawQuantity = (soleItem as { quantity?: number | null } | undefined)?.quantity;
    const normalizedQuantity = typeof rawQuantity === 'number' && Number.isFinite(rawQuantity) ? rawQuantity : 1;
    return normalizedQuantity > 1;
  }, [visibleItems]);

  type SelectionKind = 'size' | 'paper' | 'printer';

  const notifyPreOpenBlocked = useCallback((kind: SelectionKind, reason: 'noItems' | 'noSelection') => {
    sendSelectionHelpPrompt(kind, reason);
  }, [sendSelectionHelpPrompt]);

  const resolveScopedSelection = useCallback(
    (
      kind: SelectionKind,
      fallback: SelectionEntry,
    ):
      | {
          entries: SelectionEntry[];
          primary: SelectionEntry;
        }
      | null => {
      if (canonicalItems.length === 0) {
        notifyPreOpenBlocked(kind, 'noItems');
        return null;
      }

      const selectionSet = new Set(selectedItemIds.filter((id): id is string => Boolean(id)));

      if (selectionSet.size === 0) {
        notifyPreOpenBlocked(kind, 'noSelection');
        return null;
      }

      const selectedCandidates = visibleItems.reduce<SelectionEntry[]>((acc, current, currentIndex) => {
        const identifier = current.id;
        if (identifier && selectionSet.has(identifier)) {
          acc.push({ item: current, index: currentIndex });
        }
        return acc;
      }, []);

      if (selectedCandidates.length === 0) {
        notifyPreOpenBlocked(kind, 'noSelection');
        return null;
      }

      const fallbackIdentifier = fallback.item.id;
      const primary = fallbackIdentifier && selectionSet.has(fallbackIdentifier)
        ? fallback
        : selectedCandidates[0];

      return {
        entries: selectedCandidates,
        primary,
      };
    },
    [canonicalItems.length, notifyPreOpenBlocked, selectedItemIds, visibleItems],
  );

  const resolveSelectionScope = useCallback(
    (
      kind: SelectionKind,
      baseEntry: SelectionEntry,
      options?: {
        entries?: SelectionEntry[];
        mode?: 'single' | 'selection';
      },
    ): { entries: SelectionEntry[]; primary: SelectionEntry } | null => {
      if (options?.mode === 'single') {
        const entries = dedupeSelectionEntries([baseEntry]);
        return entries.length > 0 ? { entries, primary: entries[0] } : null;
      }

      if (Array.isArray(options?.entries) && options.entries.length > 0) {
        const entries = dedupeSelectionEntries(options.entries);
        if (entries.length === 0) {
          return null;
        }
        const primary = entries.find(candidate => candidate.item.id === baseEntry.item.id) ?? entries[0];
        return { entries, primary };
      }

      const scoped = resolveScopedSelection(kind, baseEntry);
      if (!scoped) {
        return null;
      }

      const entries = dedupeSelectionEntries(scoped.entries);
      if (entries.length === 0) {
        return null;
      }

      const primary = entries.find(candidate => candidate.item.id === scoped.primary.item.id)
        ?? entries[0];

      return { entries, primary };
    },
    [resolveScopedSelection],
  );

  const handleOpenPrintSizeWindow = useCallback(
    (
      item: Order['items'][number],
      index: number,
      options?: {
        entries?: SelectionEntry[];
        mode?: 'single' | 'selection';
      },
    ) => {
      if (!onUpdatePrintSize) {
        return;
      }
      if (!item?.id) {
        return;
      }

      const baseEntry: SelectionEntry = { item, index };
      const scope = resolveSelectionScope('size', baseEntry, options);
      if (!scope) {
        return;
      }

      const shouldSyncWithRibbon = options?.mode === 'selection'
        || (options?.mode !== 'single' && scope.entries.length > 1);

      if (shouldSyncWithRibbon) {
        scope.entries.forEach(({ item: scopedItem }) => {
          const identifier = scopedItem.id;
          if (identifier) {
            dispatch(setItemSelection({ itemId: identifier, selected: true }));
          }
        });

        sendSupervisorEvent({ type: 'UI.GOTO_STEP', step: 'size' });
      }

      analytics.track('UploadedImagesGrid: ui.uploaded_images_open_print_size', {
        mode: options?.mode ?? 'selection',
        selectedCount: scope.entries.length,
        itemId: scope.primary.item.id ?? null,
      });

      openPrintSizeDialog(buildPrintSizeDialogEntries(scope.entries), scope.primary.item.id ?? '');
    },
    [buildPrintSizeDialogEntries, onUpdatePrintSize, openPrintSizeDialog, resolveSelectionScope],
  );

  const handleOpenPaperTypeWindow = useCallback(
    (
      item: Order['items'][number],
      index: number,
      options?: {
        entries?: Array<{ item: Order['items'][number]; index: number }>;
        mode?: 'single' | 'selection';
      },
    ) => {
      if (!onUpdatePaperType) {
        return;
      }

      const baseEntry: SelectionEntry = { item, index };
      const scope = resolveSelectionScope('paper', baseEntry, options);
      if (!scope) {
        return;
      }

      const primaryOrderItemId = scope.primary.item.id;
      if (!primaryOrderItemId) {
        return;
      }

      const shouldSyncWithRibbon = options?.mode === 'selection'
        || (options?.mode !== 'single' && scope.entries.length > 1);

      if (shouldSyncWithRibbon) {
        scope.entries.forEach(({ item: scopedItem }) => {
          const identifier = scopedItem.id;
          if (identifier) {
            dispatch(setItemSelection({ itemId: identifier, selected: true }));
          }
        });

        sendSupervisorEvent({ type: 'UI.GOTO_STEP', step: 'paper' });
      }

      analytics.track('UploadedImagesGrid: ui.uploaded_images_open_paper', {
        mode: options?.mode ?? 'selection',
        selectedCount: scope.entries.length,
        itemId: primaryOrderItemId,
      });

      const entries: ItemDialogEntry[] = [];
      const initialSelections: Record<string, string | null> = {};

      scope.entries.forEach(({ item: entryItem, index: entryIndex }) => {
        const orderItemId = entryItem.id;
        if (!orderItemId) {
          return;
        }
        const storeItem = findStoreItem(orderItemId);
        const initialSelection = storeItem?.paperTypeId ?? entryItem.paperTypeId ?? null;
        const initialPrinter = storeItem?.printerId ?? entryItem.printerId ?? null;
        initialSelections[orderItemId] = initialSelection ?? null;
        entries.push({
          lookupKey: orderItemId,
          orderItemId,
          displayIndex: entryIndex,
          uploadedFileId: entryItem.uploadedFileId ?? null,
          priceLabel: formatPriceLabel(orderItemId),
          initialPaperTypeId: initialSelection ?? null,
          initialPrinterId: initialPrinter ?? null,
          initialWidthIn: storeItem?.widthIn ?? entryItem.widthIn ?? null,
          initialHeightIn: storeItem?.heightIn ?? entryItem.heightIn ?? null,
          initialMarginIn: storeItem?.marginIn ?? entryItem.marginIn ?? null,
          initialGalleryWrapIn: storeItem?.galleryWrapIn ?? entryItem.galleryWrapIn ?? null,
          initialGalleryWrapMirrorEdges: storeItem?.galleryWrapMirrorEdges ?? entryItem.galleryWrapMirrorEdges ?? null,
        });
      });

      if (entries.length === 0) {
        return;
      }

      const initialEntry = entries.find(entry => entry.orderItemId === primaryOrderItemId) ?? entries[0];

      setDialogStateWithRef({
        kind: 'paper',
        lookupKey: initialEntry.orderItemId,
        originOrderItemId: primaryOrderItemId,
        orderItemId: initialEntry.orderItemId,
        entries,
        selections: initialSelections,
      });
    },
    [findStoreItem, formatPriceLabel, onUpdatePaperType, resolveSelectionScope],
  );

  const handleOpenPrinterWindow = useCallback(
    (
      item: Order['items'][number],
      index: number,
      options?: {
        entries?: Array<{ item: Order['items'][number]; index: number }>;
        mode?: 'single' | 'selection';
      },
    ) => {
      if (!onUpdatePrinter) {
        return;
      }

      const baseEntry: SelectionEntry = { item, index };
      const scope = resolveSelectionScope('printer', baseEntry, options);
      if (!scope) {
        return;
      }

      const primaryOrderItemId = scope.primary.item.id;
      if (!primaryOrderItemId) {
        return;
      }

      const shouldSyncWithRibbon = options?.mode === 'selection'
        || (options?.mode !== 'single' && scope.entries.length > 1);

      if (shouldSyncWithRibbon) {
        scope.entries.forEach(({ item: scopedItem }) => {
          const identifier = scopedItem.id;
          if (identifier) {
            dispatch(setItemSelection({ itemId: identifier, selected: true }));
          }
        });

        sendSupervisorEvent({ type: 'UI.GOTO_STEP', step: 'printer' });
      }

      analytics.track('UploadedImagesGrid: ui.uploaded_images_open_printer', {
        mode: options?.mode ?? 'selection',
        selectedCount: scope.entries.length,
        itemId: primaryOrderItemId,
      });

      const entries: ItemDialogEntry[] = [];
      const initialSelections: Record<string, string | null> = {};

      scope.entries.forEach(({ item: entryItem, index: entryIndex }) => {
        const orderItemId = entryItem.id;
        if (!orderItemId) {
          return;
        }
        const storeItem = findStoreItem(orderItemId);
        const initialPrinter = storeItem?.printerId ?? entryItem.printerId ?? null;
        const initialPaper = storeItem?.paperTypeId ?? entryItem.paperTypeId ?? null;
        const initialWidthIn = storeItem?.widthIn ?? entryItem.widthIn ?? null;
        const initialHeightIn = storeItem?.heightIn ?? entryItem.heightIn ?? null;
        const initialMarginIn = storeItem?.marginIn ?? entryItem.marginIn ?? null;
        const initialGalleryWrapIn = storeItem?.galleryWrapIn ?? entryItem.galleryWrapIn ?? null;
        const initialGalleryWrapMirrorEdges = storeItem?.galleryWrapMirrorEdges ?? entryItem.galleryWrapMirrorEdges ?? null;
        initialSelections[orderItemId] = initialPrinter ?? null;
        entries.push({
          lookupKey: orderItemId,
          orderItemId,
          displayIndex: entryIndex,
          uploadedFileId: entryItem.uploadedFileId ?? null,
          priceLabel: formatPriceLabel(orderItemId),
          initialPaperTypeId: initialPaper ?? null,
          initialPrinterId: initialPrinter ?? null,
          initialWidthIn,
          initialHeightIn,
          initialMarginIn,
          initialGalleryWrapIn,
          initialGalleryWrapMirrorEdges,
        });
      });

      if (entries.length === 0) {
        return;
      }

      const initialEntry = entries.find(entry => entry.orderItemId === primaryOrderItemId) ?? entries[0];

      setDialogStateWithRef({
        kind: 'printer',
        lookupKey: initialEntry.orderItemId,
        originOrderItemId: primaryOrderItemId,
        orderItemId: initialEntry.orderItemId,
        entries,
        selections: initialSelections,
      });
    },
    [findStoreItem, formatPriceLabel, onUpdatePrinter, resolveSelectionScope],
  );

  const formatDimensionPair = useCallback((widthInches?: number, heightInches?: number) => {
    if (
      typeof widthInches !== 'number'
      || typeof heightInches !== 'number'
      || widthInches <= 0
      || heightInches <= 0
    ) {
      return null;
    }
    const formattedWidth = convertInchesToUnit(widthInches, measurementUnit).toFixed(precision);
    const formattedHeight = convertInchesToUnit(heightInches, measurementUnit).toFixed(precision);
    return `${formattedWidth}${unitLabel}×${formattedHeight}${unitLabel}`;
  }, [measurementUnit, precision, unitLabel]);

  const resolveDimensionsInches = useCallback((item: Order['items'][number]) => {
    const resolvedWidth = typeof item.widthIn === 'number' && item.widthIn > 0
      ? item.widthIn
      : typeof item.originalWidthIn === 'number' && item.originalWidthIn > 0
        ? item.originalWidthIn
        : typeof item.originalWidthPx === 'number' && typeof item.dpiX === 'number'
          && item.originalWidthPx > 0 && item.dpiX > 0
          ? item.originalWidthPx / item.dpiX
          : undefined;

    const resolvedHeight = typeof item.heightIn === 'number' && item.heightIn > 0
      ? item.heightIn
      : typeof item.originalHeightIn === 'number' && item.originalHeightIn > 0
        ? item.originalHeightIn
        : typeof item.originalHeightPx === 'number' && typeof item.dpiY === 'number'
          && item.originalHeightPx > 0 && item.dpiY > 0
          ? item.originalHeightPx / item.dpiY
          : undefined;

    return {
      widthInches: resolvedWidth,
      heightInches: resolvedHeight,
    };
  }, []);
  const selectedEntries = useMemo(() => {
    const selectedIdSet = new Set(selectedItemIds);
    return visibleItems.reduce<SelectionEntry[]>((acc, item, index) => {
      if (item.id && selectedIdSet.has(item.id)) {
        acc.push({ item, index });
      }
      return acc;
    }, []);
  }, [selectedItemIds, visibleItems]);
  const selectedCount = selectedEntries.length;
  const totalSelectableCount = visibleItems.length;
  const showSelectionSummary = selectedCount > 1 && totalSelectableCount > 0;

  type AddAttemptResult = 'added' | 'alreadyOrdered' | 'blocked';

  const attemptAddToOrder = useCallback((item: Order['items'][number]): AddAttemptResult => {
    const orderItemId = item?.id;
    if (!orderItemId) {
      return 'blocked';
    }

    const effectiveStatus = getEffectiveStatus(item);
    if (isOrderedStatus(effectiveStatus)) {
      return 'alreadyOrdered';
    }

    const missing = buildMissingFields(item);
    if (missing.length > 0) {
      sendMissingConfigurationPrompt(missing);
      return 'blocked';
    }

    const updatePayload = currentOrderId
      ? { status: 'IN_ORDER' as const, orderId: currentOrderId }
      : { status: 'IN_ORDER' as const };

    dispatch(updateOrderItem({
      orderItemId,
      data: updatePayload,
    }));

    return 'added';
  }, [buildMissingFields, currentOrderId, dispatch, getEffectiveStatus, sendMissingConfigurationPrompt]);

  const handleAddToOrder = useCallback((item: Order['items'][number]) => {
    const status = attemptAddToOrder(item);
    analytics.track('UploadedImagesGrid: ui.uploaded_images_add_to_order', {
      itemId: item.id ?? null,
      status,
    });
  }, [attemptAddToOrder]);

  const handleRemoveFromOrder = useCallback((item: Order['items'][number]) => {
    if (!item?.id) {
      return;
    }
    dispatch(updateOrderItem({
      orderItemId: item.id,
      data: { status: 'NEW', orderId: undefined },
    }));
    dispatch(setItemSelection({ itemId: item.id, selected: false }));
    analytics.track('UploadedImagesGrid: ui.uploaded_images_remove_from_order', { itemId: item.id });

    if (canonicalItems.length > 0) {
      const patchedItems = canonicalItems.map((entry) => {
        if (!entry || entry.id !== item.id) {
          return entry ?? null;
        }
        return {
          ...entry,
          status: 'NEW' as const,
          orderId: undefined,
        };
      });

      sendSupervisorEvent({ type: 'ORDER.APPLY_PATCH', patch: { items: patchedItems } });
    }
  }, [canonicalItems, dispatch]);

  const handleMenuAction = (action: MenuAction, item: Order['items'][number], index: number) => {
    const itemId = item.id;
    analytics.track('dropdown: ui.uploaded_images_item_action', {
      action,
      itemId: itemId ?? null,
      index,
    });
    switch (action) {
      case 'addToOrder': {
        handleAddToOrder(item);
        break;
      }
      case 'removeFromOrder': {
        handleRemoveFromOrder(item);
        break;
      }
      case 'deleteImage': {
        const label = t('uploadedImages.pendingDeleteLabel', 'Image {{index}}', { index: index + 1 });
        beginPendingDeletion(item, label);
        break;
      }
      case 'setPrintSize': {
        handleOpenPrintSizeWindow(item, index, { mode: 'single' });
        break;
      }
      case 'selectPaper': {
        handleOpenPaperTypeWindow(item, index, { mode: 'single' });
        break;
      }
      case 'selectPrinter': {
        handleOpenPrinterWindow(item, index, { mode: 'single' });
        break;
      }
      default:
        logging.info(`[UploadedImagesGrid] ${action} requested for item ${itemId}`);
    }
  };

  const selectionMenuItems = useMemo(() => {
    if (selectedEntries.length === 0) {
      return [] as { key: MenuAction; label: string }[];
    }

    const entries = selectedEntries;
    const anyConfigured = entries.some(({ item }) => isOrderedStatus(getEffectiveStatus(item)));
    const allOrdered = entries.every(({ item }) => isOrderedStatus(getEffectiveStatus(item)));

    const items: { key: MenuAction; label: string }[] = [];

    if (!allOrdered) {
      items.push({ key: 'addToOrder', label: addToOrderLabel });
    }
    if (anyConfigured) {
      items.push({ key: 'removeFromOrder', label: removeFromOrderLabel });
    }

    staticMenuItems.forEach((item) => {
      if (item.key === 'selectPaper' && !onUpdatePaperType) {
        return;
      }
      if (item.key === 'selectPrinter' && !onUpdatePrinter) {
        return;
      }
      items.push({ key: item.key, label: item.label });
    });

    return items;
  }, [addToOrderLabel, getEffectiveStatus, onUpdatePaperType, onUpdatePrinter, removeFromOrderLabel, selectedEntries, staticMenuItems]);

  const handleSelectionMenuAction = (action: MenuAction) => {
    if (selectedEntries.length === 0) {
      return;
    }
    analytics.track('dropdown: ui.uploaded_images_selection_action', {
      action,
      selectedCount: selectedEntries.length,
    });
    if (action === 'setPrintSize') {
      const entries = selectedEntries;
      const first = entries[0];
      handleOpenPrintSizeWindow(first.item, first.index, { entries, mode: 'selection' });
      return;
    }
    if (action === 'selectPaper') {
      const entries = selectedEntries;
      const first = entries[0];
      handleOpenPaperTypeWindow(first.item, first.index, { entries, mode: 'selection' });
      return;
    }
    if (action === 'selectPrinter') {
      const entries = selectedEntries;
      const first = entries[0];
      handleOpenPrinterWindow(first.item, first.index, { entries, mode: 'selection' });
      return;
    }

    if (action === 'addToOrder') {
      const results = selectedEntries.map(({ item }) => ({
        orderItemId: item.id ?? null,
        status: attemptAddToOrder(item),
      }));

      const addedCount = results.filter(result => result.status === 'added').length;
      const alreadyCount = results.filter(result => result.status === 'alreadyOrdered').length;
      const totalTargets = results.filter(result => result.orderItemId).length;

      if (totalTargets > 1 && (addedCount > 0 || alreadyCount > 0)) {
        const parts: string[] = [];

        parts.push(
          t('uploadedImages.multiAddSummary.intro', {
            total: totalTargets,
            added: addedCount,
            already: alreadyCount,
          }),
        );

        if (addedCount > 0) {
          const addedKey = addedCount === 1
            ? 'uploadedImages.multiAddSummary.addedOne'
            : 'uploadedImages.multiAddSummary.addedMany';
          parts.push(t(addedKey, { count: addedCount }));
        }

        if (alreadyCount > 0) {
          const alreadyKey = alreadyCount === 1
            ? 'uploadedImages.multiAddSummary.alreadyOne'
            : 'uploadedImages.multiAddSummary.alreadyMany';
          parts.push(t(alreadyKey, { count: alreadyCount }));
        }

        const summaryMessage = parts.join(' ');
        sendSupervisorEvent({ type: 'UI.ANNOUNCE_UPLOAD_SUMMARY', summaryText: summaryMessage });
      }

      return;
    }

    selectedEntries.forEach(({ item, index }) => {
      handleMenuAction(action, item, index);
    });
  };

  if (dialogState && selectedItem) {
    if (dialogState.kind === 'printSize' && onUpdatePrintSize) {
      const entries = dialogState.entries ?? [];

      const headerOptions: ItemSelectionOption[] = entries.map(entry => {
        const storeItem = findStoreItem(entry.orderItemId);
        return {
          key: entry.lookupKey,
          label: t('canvas.steps.itemNumber', 'Item {{index}}', { index: entry.displayIndex + 1 }),
          uploadedFileId: entry.uploadedFileId ?? undefined,
          priceLabel: resolvePreviewPriceLabel(entry.orderItemId) ?? undefined,
          priceLoading: previewPriceState[entry.orderItemId]?.status === 'loading',
          warningLabel: getPrintQualityWarningLabel(storeItem, t),
        } satisfies ItemSelectionOption;
      });

      const activeEntryKey = dialogState.lookupKey;
      const activeOrderItemId = dialogState.orderItemId;

      const handleSwitchEntry = (entry: ItemDialogEntry) => {
        setDialogStateWithRef(prev => {
          if (!prev || prev.kind !== 'printSize') {
            return prev;
          }
          return {
            ...prev,
            lookupKey: entry.lookupKey,
            orderItemId: entry.orderItemId,
          } satisfies DialogState;
        });
      };

      const handlePrintSizeDraftChange = (nextSize: PrintSizeValue | undefined) => {
        if (!activeOrderItemId) {
          return;
        }

        const entry = entries.find(candidate => candidate.orderItemId === activeOrderItemId);
        if (!entry) {
          return;
        }

        const isValidSize = Boolean(
          nextSize
          && typeof nextSize.widthIn === 'number'
          && nextSize.widthIn > 0
          && typeof nextSize.heightIn === 'number'
          && nextSize.heightIn > 0,
        );

        setDialogStateWithRef(prev => {
          if (!prev || prev.kind !== 'printSize') {
            return prev;
          }

          const nextDraftSizes = { ...prev.draftSizes };
          if (isValidSize && nextSize) {
            nextDraftSizes[activeOrderItemId] = nextSize;
          } else {
            delete nextDraftSizes[activeOrderItemId];
          }

          return {
            ...prev,
            draftSizes: nextDraftSizes,
          } satisfies DialogState;
        });

        if (!isValidSize || !nextSize) {
          clearPreviewDebounce(activeOrderItemId);
          updatePreviewState(activeOrderItemId, null);
          return;
        }

        const storeItem = findStoreItem(activeOrderItemId);
        const paperTypeId = storeItem?.paperTypeId ?? entry.initialPaperTypeId ?? null;
        const printerId = storeItem?.printerId ?? entry.initialPrinterId ?? null;
        void queuePreviewPrice(entry, {
          paperTypeId,
          printerId,
          widthInches: nextSize.widthIn,
          heightInches: nextSize.heightIn,
          galleryWrapInches: nextSize.galleryWrapIn,
        });
      };

      const handlePrintSizeValidityChange = (isValid: boolean) => {
        if (!activeOrderItemId) {
          return;
        }

        setDialogStateWithRef(prev => {
          if (!prev || prev.kind !== 'printSize') {
            return prev;
          }

          const currentlyInvalid = prev.invalidOrderItemIds[activeOrderItemId] === true;
          if (isValid === !currentlyInvalid) {
            return prev;
          }

          const nextInvalidOrderItemIds = { ...prev.invalidOrderItemIds };
          if (isValid) {
            delete nextInvalidOrderItemIds[activeOrderItemId];
          } else {
            nextInvalidOrderItemIds[activeOrderItemId] = true;
          }

          return {
            ...prev,
            invalidOrderItemIds: nextInvalidOrderItemIds,
          } satisfies DialogState;
        });
      };

      const handlePrintSizeDraftReset = () => {
        if (!activeOrderItemId) {
          return;
        }

        setDialogStateWithRef(prev => {
          if (!prev || prev.kind !== 'printSize') {
            return prev;
          }

          const nextDraftSizes = { ...prev.draftSizes };
          delete nextDraftSizes[activeOrderItemId];
          const nextInvalidOrderItemIds = { ...prev.invalidOrderItemIds };
          delete nextInvalidOrderItemIds[activeOrderItemId];
          return {
            ...prev,
            draftSizes: nextDraftSizes,
            invalidOrderItemIds: nextInvalidOrderItemIds,
          } satisfies DialogState;
        });

        clearPreviewDebounce(activeOrderItemId);
        updatePreviewState(activeOrderItemId, null);
      };

      return (
        <>
          <div className="flex h-full flex-col">
          <ItemSelectionHeader
            title={t('canvas.steps.selectItemForSizing', 'Choose item to size')}
            items={headerOptions}
            activeKey={activeEntryKey}
            onSelect={(key) => {
              const next = entries.find(entry => entry.lookupKey === key);
              if (next) {
                handleSwitchEntry(next);
              }
            }}
          />
          <div className="flex-1 min-h-0">
            <SetPrintSizeWindow
              key={activeOrderItemId ?? dialogState.lookupKey}
              item={selectedItem}
              onClose={closeDialog}
              onBack={handlePrintSizeBackRequest}
              onSave={handlePrintSizeSave}
              saveDisabled={Object.values(dialogState.invalidOrderItemIds ?? {}).some(Boolean)}
              saveLabel={hasUnsavedPrintSizeChanges
                ? t('setPrintSize.saveCta', 'Save size')
                : t('setPrintSize.confirmCta', 'Confirm size')}
              onRequestPrintQualityHelp={(warning) => {
                sendSupervisorEvent({
                  type: 'UI.REQUEST_AI_PROMPT',
                  aiPrompt: `Explain briefly and politely why print files should be at least ${warning.threshold} PPI for printing, and tell the user this image is currently about ${Math.round(warning.ppi)} PPI at the selected size. Ask whether they want to keep this quality or adjust the print size.`,
                  fallbackMessage: t(
                    'setPrintSize.lowPpiAiFallback',
                    'This image is {{ppi}} PPI at the selected print size. For sharp printed results, {{threshold}} PPI or higher is recommended.',
                    { ppi: Math.round(warning.ppi), threshold: warning.threshold },
                  ),
                });
              }}
              draftSize={activeOrderItemId ? dialogState.draftSizes[activeOrderItemId] ?? undefined : undefined}
              onDraftChange={handlePrintSizeDraftChange}
              onDraftValidityChange={handlePrintSizeValidityChange}
              onDraftReset={handlePrintSizeDraftReset}
            />
          </div>
        </div>
          <ConfirmAlertDialog
            open={printSizeDiscardPromptState != null}
            onOpenChange={handlePrintSizeDiscardPromptOpenChange}
            title={t('setPrintSize.discardChangesTitle', 'Discard unsaved size changes?')}
            description={t(
              'setPrintSize.discardChangesDescription',
              'Your size changes are not saved yet. You can keep editing or discard them and leave this screen.',
            )}
            confirmText={t('setPrintSize.discardChangesConfirm', 'Discard changes')}
            cancelText={t('setPrintSize.discardChangesCancel', 'Keep editing')}
            onCancel={() => {
              if (printSizeDiscardPromptState?.source === 'stepRibbon') {
                resolvePendingPrintSizeLeave(false);
              }
              setPrintSizeDiscardPromptState(null);
            }}
            onConfirm={handleConfirmPrintSizeDiscard}
          />
        </>
      );
    }
    if (dialogState.kind === 'paper' && onUpdatePaperType) {
      const entries = dialogState.entries ?? [];

      const handleSwitchEntry = (entry: ItemDialogEntry) => {
        setDialogStateWithRef(prev => {
          if (!prev || prev.kind !== 'paper') {
            return prev;
          }
          return {
            ...prev,
            lookupKey: entry.lookupKey,
            orderItemId: entry.orderItemId,
          } satisfies DialogState;
        });
      };

      const headerOptions: ItemSelectionOption[] = entries.map(entry => {
        const preview = previewPriceState[entry.orderItemId];
        const computedLabel = preview && preview.status !== 'failed' && preview.label != null
          ? preview.label
          : preview?.status === 'failed'
            ? preview.label
            : formatPriceLabel(entry.orderItemId);
        return {
          key: entry.lookupKey,
          label: t('canvas.steps.itemNumber', 'Item {{index}}', { index: entry.displayIndex + 1 }),
          uploadedFileId: entry.uploadedFileId,
          priceLabel: computedLabel,
          priceLoading: preview?.status === 'loading',
        } satisfies ItemSelectionOption;
      });
      const activeEntryKey = dialogState.lookupKey;
      const activeSelection = dialogState.selections[dialogState.orderItemId] ?? null;

      const canApplyToAll = entries.length > 1;

      return (
        <>
          <div className="flex h-full flex-col">
            <ItemSelectionHeader
              title={t('canvas.steps.selectItemForPaper', 'Choose item for paper selection')}
              items={headerOptions}
              activeKey={activeEntryKey}
              onSelect={(key) => {
                const next = entries.find(entry => entry.lookupKey === key);
                if (next) {
                  handleSwitchEntry(next);
                }
              }}
            />
            <div className="flex-1 min-h-0">
              <SetPaperTypeWindow
                item={selectedItem}
                onClose={closeDialog}
                onBack={() => handleSelectionBackRequest('paper')}
                onSave={handlePaperTypeSave}
                saveLabel={hasUnsavedPaperChanges
                  ? t('setPaperType.saveCta', 'Save paper type')
                  : t('setPaperType.confirmCta', 'Confirm paper type')}
                selectedPaperTypeId={activeSelection}
                onSelectPaperType={handlePaperSelectionChange}
                onConfirmSelection={handlePaperSelectionConfirm}
                onApplyPaperToAll={canApplyToAll ? handleApplyPaperToAllItems : undefined}
              />
            </div>
          </div>
          <ConfirmAlertDialog
            open={canvasWrapPromptState != null}
            onOpenChange={handleCanvasWrapPromptOpenChange}
            title={t('uploadedImages.canvasWrapPrompt.title', 'Add gallery wrap for canvas?')}
            description={t(
              'uploadedImages.canvasWrapPrompt.description',
              'Canvas prints usually need gallery wrap so the image can continue around the stretcher bars. We can add a default 2 inch gallery wrap to the selected canvas items now, replace any existing margin on those items, and then open size settings so you can adjust it.',
            )}
            confirmText={t('uploadedImages.canvasWrapPrompt.confirm', 'Yes, add gallery wrap')}
            cancelText={t('uploadedImages.canvasWrapPrompt.cancel', 'No')}
            onConfirm={() => void handleConfirmCanvasWrap()}
          />
          <ConfirmAlertDialog
            open={selectionDiscardPromptState != null}
            onOpenChange={handleSelectionDiscardPromptOpenChange}
            title={selectionDiscardPromptState?.kind === 'printer'
              ? t('setPrinter.discardChangesTitle', 'Discard unsaved printer changes?')
              : t('setPaperType.discardChangesTitle', 'Discard unsaved paper changes?')}
            description={selectionDiscardPromptState?.kind === 'printer'
              ? t('setPrinter.discardChangesDescription', 'Your printer selection is not saved yet. You can keep editing or discard it and leave this screen.')
              : t('setPaperType.discardChangesDescription', 'Your paper selection is not saved yet. You can keep editing or discard it and leave this screen.')}
            confirmText={t('common.discardChanges', 'Discard changes')}
            cancelText={t('common.keepEditing', 'Keep editing')}
            onCancel={() => {
              if (selectionDiscardPromptState?.source === 'stepRibbon') {
                resolvePendingSelectionLeave(false);
              }
              setSelectionDiscardPromptState(null);
            }}
            onConfirm={handleConfirmSelectionDiscard}
          />
        </>
      );
    }
    if (dialogState.kind === 'printer' && onUpdatePrinter) {
      const entries = dialogState.entries ?? [];

      const handleSwitchEntry = (entry: ItemDialogEntry) => {
        setDialogStateWithRef(prev => {
          if (!prev || prev.kind !== 'printer') {
            return prev;
          }
          return {
            ...prev,
            lookupKey: entry.lookupKey,
            orderItemId: entry.orderItemId,
          } satisfies DialogState;
        });
      };

      const headerOptions: ItemSelectionOption[] = entries.map(entry => {
        const preview = previewPriceState[entry.orderItemId];
        const computedLabel = preview && preview.status !== 'failed' && preview.label != null
          ? preview.label
          : preview?.status === 'failed'
            ? preview.label
            : formatPriceLabel(entry.lookupKey);
        return {
          key: entry.lookupKey,
          label: t('canvas.steps.itemNumber', 'Item {{index}}', { index: entry.displayIndex + 1 }),
          uploadedFileId: entry.uploadedFileId,
          priceLabel: computedLabel,
          priceLoading: preview?.status === 'loading',
        } satisfies ItemSelectionOption;
      });
      const activeEntryKey = dialogState.lookupKey;
      const activeSelection = dialogState.selections[dialogState.orderItemId] ?? null;

      const canApplyToAll = entries.length > 1;

      return (
        <div className="flex h-full flex-col">
          <ItemSelectionHeader
            title={t('canvas.steps.selectItemForPrinter', 'Choose item for printer selection')}
            items={headerOptions}
            activeKey={activeEntryKey}
            onSelect={(key) => {
              const next = entries.find(entry => entry.lookupKey === key);
              if (next) {
                handleSwitchEntry(next);
              }
            }}
          />
          <div className="flex-1 min-h-0">
            <SetPrinterWindow
              item={selectedItem}
              onClose={closeDialog}
              onBack={() => handleSelectionBackRequest('printer')}
              onSave={handlePrinterSave}
              saveLabel={hasUnsavedPrinterChanges
                ? t('setPrinter.saveCta', 'Save printer')
                : t('setPrinter.confirmCta', 'Confirm printer')}
              selectedPrinterId={activeSelection ?? null}
              onSelectPrinter={handlePrinterSelectionChange}
              onConfirmSelection={handlePrinterSelectionConfirm}
              onApplyPrinterToAll={canApplyToAll ? handleApplyPrinterToAllItems : undefined}
            />
          </div>
        </div>
      );
    }
  }

  if (dialogState) {
    // If we have a dialog state but cannot render the associated window (missing handlers), reset.
    closeDialog();
    return (
      <div />
    );
  }

  return (
    <div className="p-4 rounded-lg shadow w-full max-w-5xl mx-auto border bg-background">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-foreground">
          {t('orderSummary.uploadedImagesTitle', 'Uploaded images')}
        </h3>
        <Button type="button" onClick={handleGoToUpload} className="inline-flex items-center gap-2">
          <UploadCloud className="h-4 w-4" aria-hidden />
          <span>{t('orderSummary.goToUpload', 'Upload images')}</span>
        </Button>
      </div>
      {visibleItems.length === 0 ? (
        <div className="text-sm text-muted-foreground flex items-center justify-between gap-3 rounded-md border border-dashed border-muted-foreground/40 bg-muted/20 px-4 py-3">
          <div className="flex items-center gap-2">
            <span>{t('orderSummary.noUploadedImages', 'No images have been uploaded yet.')}</span>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {showSelectionSummary ? (
            <div className="flex items-center justify-between rounded-md border border-emerald-400/60 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-500/50 dark:text-emerald-200">
              <span className="font-medium">
                {t(
                  'uploadedImages.selectionSummary',
                  '{{selected}} of {{total}} selected',
                  { selected: selectedCount, total: totalSelectableCount },
                )}
              </span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-emerald-400/60 bg-emerald-500/10 text-emerald-800 shadow hover:bg-emerald-500/20 dark:border-emerald-500/50 dark:text-emerald-200"
                    aria-label={t('uploadedImages.selectionActionsMenu', 'Selection actions')}
                  >
                    <MoreVertical className="h-5 w-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={8} className="w-fit min-w-0 px-1 py-1">
                  {selectionMenuItems.map(action => (
                    <DropdownMenuItem
                      key={action.key}
                      className="justify-end text-right"
                      onSelect={() => {
                        handleSelectionMenuAction(action.key);
                      }}
                    >
                      {action.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : null}
          <div className="grid gap-4 grid-cols-301 sm:grid-cols-2 lg:grid-cols-3">
          {visibleItems.map((item, index) => {
            const selectableId = item.id;
            if (!selectableId) {
              return null;
            }
            const pendingMeta = pendingMap.get(selectableId);
            const isDeleting = deletingIds.includes(selectableId);
            const remainingSeconds = pendingMeta
              ? Math.max(0, Math.ceil((pendingMeta.expiresAt - now) / 1_000))
              : 0;
            const isSelected = selectedItemIds.includes(selectableId);
            const checkboxOuterClass = `inline-flex h-11 w-11 items-center justify-center rounded-full border shadow-sm transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-emerald-300/60 ${
              isSelected ? 'border-emerald-400 bg-emerald-500/20' : 'border-border/80 bg-card/50'
            }`;
            const checkboxInnerClass = `inline-flex h-8 w-8 items-center justify-center rounded-full transition-all ${
              isSelected ? 'bg-emerald-200 text-emerald-900 shadow-inner' : 'bg-muted/10 text-muted-foreground/60'
            }`;
            const checkboxIconClass = `h-5 w-5 transition-opacity ${
              isSelected ? 'opacity-100 text-emerald-900' : 'opacity-0 text-transparent'
            }`;
            const originalDimensions = item.originalWidthPx && item.originalHeightPx
              ? `${item.originalWidthPx}px × ${item.originalHeightPx}px`
              : undefined;
            const { widthInches: resolvedWidthInches, heightInches: resolvedHeightInches } = resolveDimensionsInches(item);
            const sizeInInches = formatDimensionPair(resolvedWidthInches, resolvedHeightInches) ?? undefined;
            const dpiValue = (() => {
              if (
                typeof item.originalWidthPx === 'number' &&
                typeof item.originalHeightPx === 'number' &&
                resolvedWidthInches && resolvedWidthInches > 0 &&
                resolvedHeightInches && resolvedHeightInches > 0
              ) {
                const dpiX = Math.round(item.originalWidthPx / resolvedWidthInches);
                const dpiY = Math.round(item.originalHeightPx / resolvedHeightInches);
                return dpiX === dpiY ? `${dpiX} PPI` : `${dpiX}×${dpiY} PPI`;
              }
              return undefined;
            })();
            const effectiveStatus = getEffectiveStatus(item);
            const statusLabel = effectiveStatus
              ? t(`orderSummary.status.${effectiveStatus.toLowerCase()}`, effectiveStatus)
              : undefined;
            const notAvailable = '—';
            const dimensionsLabel = t('orderSummary.originalDimensions', 'Original dimensions');
            const dpiLabel = t('orderSummary.resolutionLabel', 'Resolution');
            const sizeInLabel = t('orderSummary.sizeInInches', 'Size ({{unit}})', { unit: unitLabel });
            const statusTitle = t('orderSummary.statusLabel', 'Status');
            const paperLabel = t('orderSummary.paper', 'Paper');
            const printerLabelTitle = t('orderSummary.printer', 'Printer');
            const displayOriginalDimensions = originalDimensions ?? notAvailable;
            const displayDpi = dpiValue ?? notAvailable;
            const displaySizeIn = sizeInInches ?? notAvailable;
            const displayStatus = statusLabel ?? notAvailable;

            const storeItem = findStoreItem(selectableId);
            const missingFields = buildMissingFields(item);
            const isOrdered = isOrderedStatus(effectiveStatus);
            const canAddToOrder = !isOrdered && missingFields.length === 0;

            const paperTypeLabel = resolvePaperLabel(storeItem?.paperTypeId ?? item.paperTypeId ?? null);
            const displayPaper = paperTypeLabel ?? notAvailable;
            const printerLabel = resolvePrinterLabel(storeItem?.printerId ?? item.printerId ?? null);
            const displayPrinter = printerLabel ?? notAvailable;

            const priceLabel = formatPriceLabel(selectableId) ?? undefined;
            const printQualityWarningLabel = getPrintQualityWarningLabel(item, t);

            return (
              <article key={item.id} className="relative group rounded-lg border border-border bg-card shadow-sm overflow-hidden">
                {shouldShowSelection ? (
                  <label
                    className="absolute left-3 top-3 z-10 inline-flex items-center justify-center rounded-full"
                    aria-label={t('orderSummary.selectImage', 'Select image')}
                  >
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={isSelected}
                      onChange={(event) => handleSelectionChange(selectableId, event.target.checked)}
                    />
                    <span className={checkboxOuterClass}>
                      <span className={checkboxInnerClass}>
                        <Check strokeWidth={3} className={checkboxIconClass} aria-hidden />
                      </span>
                    </span>
                  </label>
                ) : null}
                {pendingMeta ? null : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="absolute right-3 top-3 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-border/70 bg-background/80 text-muted-foreground shadow hover:text-foreground"
                        aria-label={t('orderSummary.imageActions', 'Image actions')}
                      >
                        <MoreVertical className="h-5 w-5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" sideOffset={8} className="w-fit min-w-0 px-1 py-1">
                      {getItemMenuActions(item).map(action => (
                        <DropdownMenuItem
                          key={action.key}
                          className="justify-end text-right"
                          onSelect={() => {
                            handleMenuAction(action.key, item, index);
                          }}
                        >
                          {action.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                <div className={`p-3 flex flex-col items-center transition ${pendingMeta ? 'pointer-events-none opacity-60' : ''}`}>
                  <OrderImagePreview
                    imageId={item.uploadedFileId}
                    originalWidthPx={item.originalWidthPx ?? undefined}
                    originalHeightPx={item.originalHeightPx ?? undefined}
                    previewBox={140}
                    alt={t('orderSummary.imageAlt', 'Uploaded image preview')}
                    magnifyLabel={t('orderSummary.magnify', 'View details')}
                    zoomLabel={t('orderSummary.zoom', 'Zoom')}
                    placeholderLabel={t('orderSummary.noImage', 'No image available')}
                    showDownload={false}
                    className="bg-muted rounded-md"
                    priceLabel={priceLabel}
                    priceRibbonPosition={{ top: '0.45rem', right: '0' }}
                    warningLabel={printQualityWarningLabel}
                    widthIn={item.widthIn ?? null}
                    heightIn={item.heightIn ?? null}
                    marginIn={item.marginIn ?? null}
                    galleryWrapIn={item.galleryWrapIn ?? null}
                  />
                  <div className="w-full mt-2 text-left">
                    <div className="flex items-center justify-between gap-3">
                      <p className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        {printQualityWarningLabel ? (
                          <AlertTriangle
                            className="h-3.5 w-3.5 shrink-0 text-amber-600"
                            aria-label={printQualityWarningLabel}
                          />
                        ) : null}
                        {t('orderSummary.itemLabel', 'Item {{index}}', { index: index + 1 })}
                      </p>
                      <OrderItemQuantityField
                        itemId={item.id}
                        label={t('uploadedImages.quantity.label', 'Qty')}
                        ariaLabel={t('uploadedImages.quantity.inputLabel', 'Quantity')}
                        value={quantityDrafts[item.id] ?? String(resolveItemQuantity(storeItem?.quantity ?? item.quantity))}
                        onChange={(value) => handleQuantityDraftChange(item.id, value)}
                        onBlur={() => { void handleQuantityCommit(item.id); }}
                      />
                    </div>
                  </div>
                </div>
                <div className="px-3 pb-3 space-y-3 border-t border-border pt-3">
                  <div className="grid grid-cols-2 gap-2 text-sm text-foreground/90">
                    <span title={dimensionsLabel} className="truncate" aria-label={dimensionsLabel}>
                      {displayOriginalDimensions}
                    </span>
                    <span title={dpiLabel} className="truncate text-right" aria-label={dpiLabel}>
                      {displayDpi}
                    </span>
                    <span title={sizeInLabel} className="truncate" aria-label={sizeInLabel}>
                      {displaySizeIn}
                    </span>
                    <span title={statusTitle} className="flex items-center justify-end gap-1 text-right" aria-label={statusTitle}>
                      {isOrdered ? (
                        <button
                          type="button"
                          onClick={() => handleRemoveFromOrder(item)}
                          className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                          title={removeFromOrderLabel}
                          aria-label={removeFromOrderLabel}
                        >
                          {inOrderLabel}
                          <Check className="h-3.5 w-3.5" aria-hidden />
                        </button>
                      ) : canAddToOrder ? (
                        <button
                          type="button"
                          onClick={() => handleAddToOrder(item)}
                          className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-3 py-1 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                        >
                          {addToOrderLabel}
                          <Check className="h-3.5 w-3.5" aria-hidden />
                        </button>
                      ) : (
                        <>
                          <span className="truncate max-w-full text-right">{displayStatus}</span>
                          {statusLabel ? <Info className="w-3 h-3 text-muted-foreground" aria-hidden /> : null}
                        </>
                      )}
                    </span>
                    <span className="col-span-2 truncate text-xs text-muted-foreground" aria-label={paperLabel}>
                      <span className="font-medium text-foreground/80">{paperLabel}:</span>{' '}
                      <span className="text-foreground/90">{displayPaper}</span>
                    </span>
                    <span className="col-span-2 truncate text-xs text-muted-foreground" aria-label={printerLabelTitle}>
                      <span className="font-medium text-foreground/80">{printerLabelTitle}:</span>{' '}
                      <span className="text-foreground/90">{displayPrinter}</span>
                    </span>
                  </div>
                  <div className="h-px w-full bg-border/70" />
                  <div className="grid grid-cols-2 gap-3 rounded-md bg-card/30 p-2 md:grid-cols-4">
                    {controlButtons.map(({ key, label }) => {
                      const resolvedWidthIn = storeItem?.widthIn ?? item.widthIn;
                      const resolvedHeightIn = storeItem?.heightIn ?? item.heightIn;
                      const resolvedPaperId = storeItem?.paperTypeId ?? item.paperTypeId;
                      const resolvedPrinterId = storeItem?.printerId ?? item.printerId;

                      const isConfigured = (() => {
                        switch (key) {
                          case 'printSize':
                            return (
                              typeof resolvedWidthIn === 'number'
                              && resolvedWidthIn > 0
                              && typeof resolvedHeightIn === 'number'
                              && resolvedHeightIn > 0
                            );
                          case 'paper':
                            return typeof resolvedPaperId === 'string' && resolvedPaperId.length > 0;
                          case 'printer':
                            return typeof resolvedPrinterId === 'string' && resolvedPrinterId.length > 0;
                          default:
                            return false;
                        }
                      })();

                      const iconWrapperClassName = isConfigured
                        ? 'border-emerald-500/30 bg-emerald-500/15 text-emerald-600'
                        : 'border-muted-foreground/20 bg-muted/10 text-muted-foreground/60';
                      const IconComponent = isConfigured ? Check : Info;

                      return (
                        <button
                          key={key}
                          type="button"
                          title={label}
                          aria-label={label}
                          className="inline-flex h-16 w-full items-center justify-center rounded-lg border border-border/40 bg-transparent text-muted-foreground/70 hover:bg-muted/20"
                          onClick={
                            key === 'printSize'
                              ? () => handleOpenPrintSizeWindow(item, index, { mode: 'single' })
                              : key === 'paper'
                                ? () => handleOpenPaperTypeWindow(item, index, { mode: 'single' })
                                : key === 'printer'
                                  ? () => handleOpenPrinterWindow(item, index, { mode: 'single' })
                                  : undefined
                          }
                          disabled={
                            (key === 'printSize' && !onUpdatePrintSize) ||
                            (key === 'paper' && !onUpdatePaperType) ||
                            (key === 'printer' && !onUpdatePrinter)
                          }
                        >
                          <span className="inline-flex flex-col items-center gap-1">
                            <span className={`inline-flex h-8 w-8 items-center justify-center rounded-md border transition-colors ${iconWrapperClassName}`}>
                              <IconComponent className="h-5 w-5" aria-hidden />
                            </span>
                            <span className="text-[11px] font-medium text-muted-foreground/60 whitespace-nowrap">{label}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {pendingMeta ? (
                  <div className="absolute inset-0 z-20 flex flex-col bg-background/40 backdrop-blur-sm">
                    <div className="pointer-events-auto border-b border-border/70 bg-background/95 px-3 py-3 shadow-md">
                      <div className="flex flex-col items-center gap-3 text-center">
                        <div className="text-sm font-medium text-foreground">
                          {t('uploadedImages.deletePendingTitle', '{{label}} marked for deletion', {
                            label: pendingMeta.label,
                          })}
                        </div>
                        <div className="flex items-center justify-center gap-4">
                          <span className="mt-1 text-xs text-muted-foreground">
                            {t('uploadedImages.deleteCountdown', 'Removing in {{seconds}}s', {
                              seconds: remainingSeconds,
                            })}
                          </span>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 rounded-md border border-border/70 bg-card px-3.5 py-1.5 text-xs font-semibold text-foreground hover:bg-card/80 disabled:opacity-60"
                            onClick={() => handleUndoDeletion(pendingMeta.orderItemId)}
                            disabled={isDeleting}
                          >
                            <Undo2 className="h-4 w-4" aria-hidden />
                            {t('common.undo', 'Undo')}
                          </button>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 rounded-md bg-destructive px-3.5 py-1.5 text-xs font-semibold text-destructive-foreground hover:bg-destructive/90 disabled:opacity-60"
                            onClick={() => finalizeDeletion(pendingMeta.orderItemId)}
                            disabled={isDeleting}
                          >
                            {isDeleting
                              ? t('uploadedImages.deleting', 'Deleting…')
                              : t('uploadedImages.confirmDelete', 'Delete now')}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1" />
                  </div>
                ) : null}
              </article>
            );
          })}
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadedImagesGrid;
